import { useState, useCallback, useEffect, useRef } from "react";
import {
  extractWindowedPresentationContent,
  applyModifications,
  getSelectedTableData,
  exportSlideAsImage,
  exportPresentationAsBase64Compressed,
  classifyApplyResult,
  countSuccessfulOps,
  listSlides,
  isLogoImageUrl,
} from "../services/slideService";
import { CHAT_CONTEXT_WINDOW_SIZE } from "../config/constants";
import { shouldUseFactSetBridge } from "../services/presentationHost";
import { isPowerPointNativeAgentActive, queryOpenAIStreaming } from "../services/ai";
import {
  applyPreslinkLinkedMarkers,
  excludePreslinkManagedShapeMods,
  getPreslinkTurnBlockReason,
  PRESLINK_NATIVE_EDIT_BLOCKED_MESSAGE,
  probeSelectedSlidePreslinkRefs,
} from "../services/ai/agentEditTools";
import {
  applyCreatedPreslinkChartMarkers,
  readCreatedPreslinkCharts,
} from "../services/ai/preslinkChartAgentTools";
import {
  createPptxLangfuseRunContext,
  postPptxLangfuseObservation,
  postPptxLangfuseOutcome,
  startPptxLangfuseRun,
  summarizePresentationForLangfuse,
  toLangfuseRunSkills,
} from "../langfuseTrace";
import {
  emptyTimeline,
  reduceAgentEvent,
  capAgentTimeline,
  type AgentTimelineState,
} from "../services/ai/agentStream";
import { getReasoningNarrationConfig } from "../services/ai/narratorConfig";
import { createNarratingAgentEventSink } from "../services/ai/reasoningNarrator";
import {
  getBrandContext,
  isResearchToolName,
  type AgentToolContext,
  type ToolExecutionPolicy,
} from "../services/ai/types";
import {
  applySkillRunContextToRuntime,
  createSkillResolveSingleFlight,
  createSkillSubmissionId,
  snapshotSkillSubmissionOptions,
  type SkillRunContext,
  type SkillSubmissionInput,
  type SkillTurnBootstrapResult,
} from "../services/ai/skills";
import type { SelectedSkillReference, SkillDecision } from "../../shared/skills";
import {
  messagesBeforeLastUser,
  replaceSkillRun,
  SKILL_RUN_ERRORS,
} from "../../shared/skills/runtime/skillDecision";
import { skillLoadingStatus } from "../../shared/skills/runtime/skillLoadingStatus";
import type { BrandId } from "../services/ai/brandRegistry";
import { resolveSnippetPolicy } from "../services/ai/snippetPolicy";
import {
  getDeckPlanTransientContext,
  reattachDeckPlanTransientContext,
  stripDeckPlanTransientContext,
  type DeckPlan,
  type DeckPlanTransientContext,
  type ReviewSkillBindingState,
} from "../services/ai/deckPlannerService";
import { buildPendingDeckPlanPrompt } from "../services/ai/deckPlanAgentTools";
import {
  appendPlanningEvidence,
  createReviewOperationId,
  withPlanningEvidenceSkillBinding,
  type PlanningEvidencePack,
} from "../services/ai/planningEvidence";
import {
  PresentationModification,
  PresentationContent,
  ChatMessage,
  ModificationCitation,
  generateId,
  ChartInfo,
  ApplyResultSummary,
  ApplyDiagnostic,
  TabsState,
  Tab,
  XlChartType,
  SlideCitationBinding,
  ChatTaskContext,
  createPendingSkillTurnMetadata,
  createSkillTurnMetadata,
  type SkillTurnMetadata,
} from "../types";
import {
  createTab,
  getDocumentId,
  loadTabsStateForDocumentId,
  saveTabsStateForDocumentId,
  updateTab,
} from "../services/storageService";
import { capturePresentationKey } from "../services/presentationIdentity";
import type { FactSetPresentationRef } from "../services/factset/intentBridgeService";
import {
  registerTabOperation,
  registerThinkingPhase,
  retargetThinkingPhaseTicket,
  unregisterThinkingPhase,
} from "../services/inFlightTabRegistry";
import {
  mergeApplySummaries,
  buildApplyStatusMessage,
  findPrimaryIssue,
  prepareRequestData,
  buildPreslinkProcessingPrompt,
  snapshotCompletedChatHistoryForRun,
  sanitizeUserFacingText,
  attachSyntheticMessageToSlideTab,
  CHAT_GENERATED_MESSAGE_PREFIX,
} from "../utils";
import {
  buildSkillPreludeMessages,
  buildTaskContextPrompt,
  withPlannerModeInstruction,
} from "../services/ai/promptCatalog";
import { isGoldenArtifactCaptureEnabled } from "../config";
import type { FinsterQueryDataSource } from "../finsterApi";
import { snapshotRunResearchSelection } from "../../shared/researchSelection";
import { snapshotPowerPointChartRoutingGate } from "../services/featureFlagService";
import { isPreslinkCreateDataConfigured } from "../../shared/factset/preslinkCreationCapability";
import { redactLocalPathText } from "../../shared/utils/localPathRedaction";
import {
  getPreslinkCreationPresentationContextViaIntents,
  getSelectedShapesViaIntents,
} from "../services/factset/intentBridgeService";
import { extractIdsFromCitation, mergeIntoPool, type Citation } from "../citations";
import {
  createGoldenArtifactRun,
  uploadGoldenArtifactJson,
  uploadGoldenArtifactPptx,
} from "../services/goldenArtifactsService";
import {
  createToolArtifactSubdirectoryAllocator,
  runGoldenCaptureStep,
} from "../utils/goldenCapture";
import {
  buildQuerySkillsAnalytics,
  trackQueryRun,
  trackQuerySubmitted,
} from "../services/analytics/product";

interface QueryResult {
  success: boolean;
  message: string;
  changes?: ChangeDescription[];
  thinking?: string;
  charts?: ChartInfo[];
}

interface ChangeDescription {
  action: string;
  description: string;
}

interface UseQueryHandlerProps {
  activeTab: Tab | undefined;
  updateActiveTab: (updates: Partial<Tab>) => void;
  setTabsState: React.Dispatch<React.SetStateAction<TabsState>>;
  syncTabsWithPresentation: (reason: string) => Promise<void>;
  taskContext: ChatTaskContext | null;
  aiMode: "chat" | "create" | "review";
  log: (message: string) => void;
  brandId?: BrandId | null;
  plannerMode?: boolean;
  companyId?: string;
  accountScopeKey?: string | null;
  pendingDeckPlan?: DeckPlan | null;
  onPresentDeckPlan?: (
    plan: DeckPlan,
    options?: { brandId?: BrandId | null }
  ) => void | Promise<void>;
  onUpdateDeckPlan?: (plan: DeckPlan) => boolean | void;
  getPendingDeckPlan?: () => DeckPlan | undefined;
  isReviewSessionOpen?: () => boolean;
  /**
   * Fired when a chat query mints a new Review operation. Snapshot Brand
   * Defaults here so Create uses the toggle from plan time, not Review-open
   * time. Follow-ups that reuse an open Review operation must not recapture.
   */
  onReviewPlanQueryStarted?: (reviewOperationId: string) => void;
  onReviewPlanningEvidence?: (
    reviewOperationId: string,
    reviewOperationGeneration: number,
    planningEvidence: PlanningEvidencePack
  ) => void;
  onReviewOperationActivity?: (
    reviewOperationId: string,
    reviewOperationGeneration: number,
    activity: Promise<void>
  ) => boolean | void;
  onReviewSkillContextResolved?: (
    reviewOperationId: string,
    reviewOperationGeneration: number,
    reviewSkillBindingState: Exclude<ReviewSkillBindingState, "unbound">,
    skillRunContext: SkillRunContext | undefined
  ) => boolean | void;
  /** The submission whose resolve result may update the matching-skill banner. */
  onSkillSubmission?: (submissionId: string) => void;
  /**
   * Fired when skills resolve for a submission. Ignored unless that submission
   * is still the one announced through `onSkillSubmission`.
   */
  onSkillDecision?: (
    submissionId: string,
    decision: SkillDecision | null,
    prompt?: string
  ) => void;
  /** User cancel. Drops the banner even if that submission resolves later. */
  onDismissSkillDecision?: () => void;
}

interface SlideSessionRef {
  id: string;
  index: number;
  slideNumber?: number;
}

function getSlideNumber(slide: { index: number; slideNumber?: number }): number {
  return slide.slideNumber ?? slide.index + 1;
}

const USER_FRIENDLY_TIMEOUT_MESSAGE =
  "Sorry your request could not be completed at this time. Please try again.";

export { PRESLINK_NATIVE_EDIT_BLOCKED_MESSAGE };

const CHART_TYPE_FRIENDLY_LABELS: Record<XlChartType, string> = {
  xlColumnClustered: "column",
  xlColumnStacked: "stacked column",
  xlColumnStacked100: "100% stacked column",
  xlBarClustered: "bar",
  xlBarStacked: "stacked bar",
  xlBarStacked100: "100% stacked bar",
  xlLine: "line",
  xlLineMarkers: "line",
  xlLineStacked: "stacked line",
  xlLineStackedMarkers: "stacked line",
  xlPie: "pie",
  xlPieExploded: "exploded pie",
  xlDoughnut: "doughnut",
  xlDoughnutExploded: "exploded doughnut",
  xlArea: "area",
  xlAreaStacked: "stacked area",
  xlAreaStacked100: "100% stacked area",
  xlXYScatter: "scatter",
  xlXYScatterLines: "scatter",
  xlXYScatterLinesNoMarkers: "scatter",
  xlBubble: "bubble",
  xlBubble3DEffect: "bubble",
  xlRadar: "radar",
  xlRadarFilled: "filled radar",
};

function getFriendlyChartTypeLabel(chartType: XlChartType | undefined): string {
  if (!chartType) return "chart";
  return CHART_TYPE_FRIENDLY_LABELS[chartType] ?? "chart";
}

interface ActiveQueryAbort {
  tabId: string;
  controller: AbortController;
  reviewOperationId?: string;
  reviewOperationGeneration?: number;
  settleReviewOperationActivity?: () => void;
}

interface ReviewSkillBinding {
  reviewOperationId?: string;
  state: ReviewSkillBindingState;
  context: SkillRunContext | undefined;
}

export function resolveInheritedReviewSkillContext(input: {
  pendingDeckPlan?: DeckPlan | null;
  currentBinding?: ReviewSkillBinding | null;
  selectionProvided: boolean;
}): SkillRunContext | undefined {
  if (!input.pendingDeckPlan) return undefined;
  if (input.selectionProvided) return undefined;
  const binding =
    input.currentBinding ??
    (() => {
      const context = getDeckPlanTransientContext(input.pendingDeckPlan);
      return context
        ? {
            reviewOperationId: context.reviewOperationId,
            state: context.reviewSkillBindingState,
            context: context.skillRunContext,
          }
        : null;
    })();
  return binding?.state === "bound" ? binding.context : undefined;
}

export function shouldInheritReviewSkillBinding(
  binding: ReviewSkillBinding | null | undefined,
  selectionProvided: boolean
): boolean {
  return !selectionProvided && (binding?.state === "bound" || binding?.state === "cleared");
}

type ActiveQueryAbortRegistry = Record<string, ActiveQueryAbort | undefined>;

export function abortAllActiveQueries(activeQueries: ActiveQueryAbortRegistry): string[] {
  const cancelledTabIds: string[] = [];
  for (const [registryKey, activeQuery] of Object.entries(activeQueries)) {
    if (!activeQuery) continue;
    activeQuery.controller.abort();
    activeQuery.settleReviewOperationActivity?.();
    cancelledTabIds.push(activeQuery.tabId);
    delete activeQueries[registryKey];
  }
  return cancelledTabIds;
}

export function abortActiveQueriesForReviewOperation(
  activeQueries: ActiveQueryAbortRegistry,
  reviewOperationId: string
): string[] {
  const cancelledTabIds: string[] = [];
  for (const [registryKey, activeQuery] of Object.entries(activeQueries)) {
    if (!activeQuery || activeQuery.reviewOperationId !== reviewOperationId) {
      continue;
    }
    activeQuery.controller.abort();
    activeQuery.settleReviewOperationActivity?.();
    cancelledTabIds.push(activeQuery.tabId);
    delete activeQueries[registryKey];
  }
  return cancelledTabIds;
}

export function getCappedAgentTimelineForMessage(
  timeline: AgentTimelineState
): AgentTimelineState | undefined {
  const hasActivity = timeline.hops.length > 0 || (timeline.activeSkills?.length ?? 0) > 0;
  return hasActivity ? capAgentTimeline(timeline) : undefined;
}

export function shouldCommitReviewSkillContext(
  status: SkillTurnBootstrapResult["status"]
): boolean {
  return status === "resolved" || status === "inherited" || status === "cleared";
}

const clearedQueryLoadingState: Pick<
  Tab,
  | "isLoading"
  | "loadingStatus"
  | "streamingThinking"
  | "agentTimeline"
  | "thoughtProcessCollapseSignal"
  | "visionFeedback"
  | "refinementStep"
> = {
  isLoading: false,
  loadingStatus: "",
  streamingThinking: "",
  agentTimeline: undefined,
  thoughtProcessCollapseSignal: undefined,
  visionFeedback: "",
  refinementStep: 0,
};

export function buildQueryStartTabUpdate(
  currentTab: Tab,
  userMessage: ChatMessage,
  timestamp: number
): Partial<Tab> {
  return {
    ...clearedQueryLoadingState,
    isLoading: true,
    session: {
      ...currentTab.session,
      messages: [...currentTab.session.messages, userMessage],
      updatedAt: timestamp,
    },
  };
}

export function queryOwnsLoadingState(
  activeQueries: ActiveQueryAbortRegistry,
  sourceTabId: string,
  controller: AbortController
): boolean {
  const activeQuery = activeQueries[sourceTabId];
  return activeQuery?.tabId === sourceTabId && activeQuery.controller === controller;
}

/**
 * Only sync tabs when the active document is still the one the query ran against.
 * Guards against overwriting another file's tab list after the user switches
 * documents mid-run (e.g. PresLink opening a replacement deck).
 */
export function shouldSyncTabsForSourceDocument(
  activeDocumentId: string,
  sourceDocumentId: string
): boolean {
  return activeDocumentId === sourceDocumentId;
}

export function clearQueryLoadingStateForTab(state: TabsState, tabId: string): TabsState {
  return updateTab(state, tabId, clearedQueryLoadingState);
}

type TerminalPreslinkGuardContext = Pick<
  AgentToolContext,
  "preslinkMutationFailedReason" | "preslinkRefreshFailed" | "preslinkExcludedShapeIdsBySlideId"
>;

type TerminalPreslinkGuardResult =
  | { status: "blocked"; error: string }
  | (ReturnType<typeof excludePreslinkManagedShapeMods> & { status: "ok" });

/** Guard terminal edit payloads with the same PresLink fail-closed policy as apply_edits. */
export function guardTerminalPreslinkModifications(
  modifications: PresentationModification,
  agentToolContext: TerminalPreslinkGuardContext | undefined
): TerminalPreslinkGuardResult {
  const preslinkBlockReason = getPreslinkTurnBlockReason(agentToolContext);
  if (preslinkBlockReason) {
    return { status: "blocked", error: preslinkBlockReason };
  }
  return {
    status: "ok",
    ...excludePreslinkManagedShapeMods(
      modifications,
      agentToolContext?.preslinkExcludedShapeIdsBySlideId
    ),
  };
}

/** Rebind a source tab after PresLink replaced its slide and changed every host id. */
export function rebindTabToRefreshedSlide(
  state: TabsState,
  params: {
    tabId: string;
    slide: SlideSessionRef;
    userMessageId?: string;
  }
): TabsState {
  const currentTab = state.tabs.find((tab) => tab.id === params.tabId);
  if (!currentTab || !params.slide.id) return state;
  const slideNumber = params.slide.slideNumber ?? params.slide.index + 1;
  return updateTab(state, params.tabId, {
    session: {
      ...currentTab.session,
      slideId: params.slide.id,
      slideIndex: params.slide.index,
      slideNumber,
      name: `Slide ${slideNumber}`,
      messages: currentTab.session.messages.map((message) =>
        params.userMessageId && message.id === params.userMessageId
          ? {
              ...message,
              slideIndex: params.slide.index,
              slideNumber,
            }
          : message
      ),
      updatedAt: Date.now(),
    },
  });
}

function toUserFriendlyRequestError(message: string): string {
  const normalized = message.toLowerCase();
  const isTimeoutLikeFailure =
    normalized.includes("load failed") ||
    normalized.includes("webkit timeout") ||
    normalized.includes("timeout") ||
    normalized.includes("network");
  return isTimeoutLikeFailure ? USER_FRIENDLY_TIMEOUT_MESSAGE : message;
}

function createSyntheticApplySummary(modifications: PresentationModification): ApplyResultSummary {
  const requestedByAction = { create: 0, modify: 0, delete: 0, duplicate: 0 };
  let requestedShapes = 0;
  for (const slide of modifications.slides || []) {
    if ((slide.action || "modify") === "duplicate") {
      requestedByAction.duplicate += 1;
    }
    for (const shape of slide.shapes || []) {
      requestedShapes += 1;
      requestedByAction[shape.action] += 1;
    }
  }

  const summary: ApplyResultSummary = {
    requested: {
      slides: modifications.slides?.length || 0,
      shapes: requestedShapes,
      byAction: requestedByAction,
    },
    applied: {
      slides: 0,
      shapes: 0,
      byAction: { create: 0, modify: 0, delete: 0, duplicate: 0 },
    },
    skipped: {
      count: 0,
      byReason: {},
      issues: [],
    },
    errors: {
      count: 0,
      byReason: {},
      issues: [],
    },
    classification: "true_no_op",
  };

  summary.classification = classifyApplyResult(summary);
  return summary;
}

export function appendModelDiagnosticsToApplySummary(
  summary: ApplyResultSummary,
  diagnostics: ApplyDiagnostic[] = []
): ApplyResultSummary {
  for (const diagnostic of diagnostics) {
    summary.skipped.count += 1;
    summary.skipped.byReason[diagnostic.code] =
      (summary.skipped.byReason[diagnostic.code] || 0) + 1;
    summary.skipped.issues.push(diagnostic);
  }
  summary.classification = classifyApplyResult(summary);
  return summary;
}

export function collectAffectedSlides(modifications: PresentationModification): SlideSessionRef[] {
  const deduped = new Map<string, SlideSessionRef>();
  for (const slide of modifications.slides || []) {
    const hasSlideOperation =
      (slide.action && slide.action !== "modify") || (slide.shapes?.length || 0) > 0;
    if (!hasSlideOperation) {
      continue;
    }
    const key = slide.id ? `id:${slide.id}` : `index:${slide.index}`;
    if (!deduped.has(key)) {
      deduped.set(key, { id: slide.id || "", index: slide.index });
    }
  }
  return Array.from(deduped.values());
}

export interface GeneratedSlideCitationMessage {
  slide: SlideSessionRef;
  message: ChatMessage;
}

export function filterCitationPayloadByIds(input: {
  citations?: Citation[];
  citedValuesByUuid?: Record<string, string>;
  citationIds?: string[];
}): {
  citations?: Citation[];
  citationsById?: Record<string, number>;
  citedValuesByUuid?: Record<string, string>;
} {
  const requestedIds = Array.isArray(input.citationIds)
    ? input.citationIds.filter((id): id is string => typeof id === "string" && id.trim().length > 0)
    : [];
  if (requestedIds.length === 0) return {};

  const requestedIdSet = new Set(requestedIds);
  const citedValuesByUuid: Record<string, string> = {};
  for (const id of requestedIds) {
    const value = input.citedValuesByUuid?.[id];
    if (typeof value === "string" && value.length > 0) {
      citedValuesByUuid[id] = value;
    }
  }
  if (Object.keys(citedValuesByUuid).length === 0) return {};

  const citations = (input.citations || []).filter((citation) =>
    extractIdsFromCitation(citation).some((id) => requestedIdSet.has(id))
  );
  if (citations.length === 0) return {};

  const citationsById: Record<string, number> = {};
  citations.forEach((citation, index) => {
    for (const id of extractIdsFromCitation(citation)) {
      if (requestedIdSet.has(id)) {
        citationsById[id] = index;
      }
    }
  });

  return { citations, citationsById, citedValuesByUuid };
}

export function findSourceSlideCitationIds(input: {
  slides: PresentationModification["slides"];
  selectedSlideId?: string;
  selectedSlideIndex: number;
}): string[] | undefined {
  const sourceSlide = (input.slides || []).find((slide) => {
    const slideAction = slide.action || "modify";
    if (slideAction !== "modify") return false;
    if (input.selectedSlideId && slide.id === input.selectedSlideId) return true;
    return slide.index === input.selectedSlideIndex;
  });
  return sourceSlide?.citationIds;
}

export function buildSourceSlideCitationPayload(input: {
  slides: PresentationModification["slides"];
  selectedSlideId?: string;
  selectedSlideIndex: number;
  citations?: Citation[];
  citationsById?: Record<string, number>;
  citedValuesByUuid?: Record<string, string>;
}): {
  citations?: Citation[];
  citationsById?: Record<string, number>;
  citedValuesByUuid?: Record<string, string>;
} {
  const sourceSlide = (input.slides || []).find((slide) => {
    const slideAction = slide.action || "modify";
    if (slideAction !== "modify") return false;
    if (input.selectedSlideId && slide.id === input.selectedSlideId) return true;
    return slide.index === input.selectedSlideIndex;
  });

  if (!sourceSlide) return {};
  if (Array.isArray(sourceSlide.citationIds)) {
    return filterCitationPayloadByIds({
      citations: input.citations,
      citedValuesByUuid: input.citedValuesByUuid,
      citationIds: sourceSlide.citationIds,
    });
  }

  // The source slide was edited but declared no per-slide citationIds. We do
  // NOT fall back to the full turn pool: those cited values may belong to OTHER
  // slides edited this turn, and attaching them here would wrongly populate the
  // source slide's Citations tab. Turn-level retrieved sources are surfaced
  // separately via the message's `retrievedCitations` (chat bubble only).
  return {};
}

/**
 * Build per-slide SlideCitationsView messages from real-id-keyed citation
 * bindings (the apply pipeline's manifest). Bindings sharing a real slide id are
 * merged (unioned ids) so each slide gets exactly one message. Replaces the old
 * positional/diff-based pairing - the binding already carries the real id.
 */
export function buildSlideCitationMessages(input: {
  bindings: SlideCitationBinding[];
  citations?: Citation[];
  citedValuesByUuid?: Record<string, string>;
  timestamp?: number;
  log?: (message: string) => void;
}): GeneratedSlideCitationMessage[] {
  const timestamp = input.timestamp ?? Date.now();

  const bySlideId = new Map<
    string,
    { slideId: string; slideIndex: number; slideNumber?: number; citationIds: string[] }
  >();
  for (const binding of input.bindings || []) {
    if (!binding.slideId) continue;
    const existing = bySlideId.get(binding.slideId);
    if (!existing) {
      bySlideId.set(binding.slideId, {
        slideId: binding.slideId,
        slideIndex: binding.slideIndex,
        slideNumber: binding.slideNumber,
        citationIds: [...binding.declaredCitationIds],
      });
      continue;
    }
    for (const id of binding.declaredCitationIds) {
      if (!existing.citationIds.includes(id)) existing.citationIds.push(id);
    }
  }

  const messages: GeneratedSlideCitationMessage[] = [];
  for (const entry of Array.from(bySlideId.values())) {
    if (entry.citationIds.length === 0) continue;
    const payload = filterCitationPayloadByIds({
      citations: input.citations,
      citedValuesByUuid: input.citedValuesByUuid,
      citationIds: entry.citationIds,
    });
    if (!payload.citations || !payload.citedValuesByUuid) {
      input.log?.(
        `[CITATIONS][WARN] Slide ${entry.slideNumber ?? entry.slideIndex + 1} declared ` +
          `${entry.citationIds.length} citation id(s), but none resolved to a source; skipping.`
      );
      continue;
    }

    const slideNumber = entry.slideNumber ?? entry.slideIndex + 1;
    messages.push({
      slide: { id: entry.slideId, index: entry.slideIndex, slideNumber },
      message: {
        id: generateId(),
        role: "assistant",
        content: `${CHAT_GENERATED_MESSAGE_PREFIX} Slide ${slideNumber}`,
        timestamp,
        slideIndex: entry.slideIndex,
        slideNumber,
        citations: payload.citations,
        citationsById: payload.citationsById,
        citedValuesByUuid: payload.citedValuesByUuid,
      },
    });
  }

  return messages;
}

export function buildNonSourceSlideCitationMessages(input: {
  bindings: SlideCitationBinding[];
  selectedSlideId?: string;
  selectedSlideIndex: number;
  citations?: Citation[];
  citedValuesByUuid?: Record<string, string>;
  timestamp?: number;
  log?: (message: string) => void;
}): GeneratedSlideCitationMessage[] {
  return buildSlideCitationMessages({
    bindings: input.bindings.filter((binding) =>
      input.selectedSlideId
        ? binding.slideId !== input.selectedSlideId
        : binding.slideIndex !== input.selectedSlideIndex
    ),
    citations: input.citations,
    citedValuesByUuid: input.citedValuesByUuid,
    timestamp: input.timestamp,
    log: input.log,
  });
}

function stripPerSlideCitationPayload(message: ChatMessage): ChatMessage {
  const nextMessage = { ...message };
  delete nextMessage.citations;
  delete nextMessage.citationsById;
  delete nextMessage.citedValuesByUuid;
  return nextMessage;
}

// `summarizeAppliedChangesForSlide` and `performEditModelRefinementImpl` were
// removed with the legacy chat vision refiner. The unified agent reviews and
// corrects its own work via get_slides_info + apply_edits.

export function mirrorAssistantMessageToAffectedSlides(
  prev: TabsState,
  activeTabId: string,
  assistantMessage: ChatMessage,
  affectedSlides: SlideSessionRef[],
  // Cloned onto affected (non-source) tabs ahead of the assistant
  // message so the user sees the originating query, not a lone
  // "Applied N changes" row. Source tab already has it.
  userMessage?: ChatMessage
): TabsState {
  let activeTab = prev.tabs.find((tab) => tab.id === activeTabId);
  let nextTabs = [...prev.tabs];

  // H2 fix: previously when `activeTabId` no longer existed in `prev.tabs`
  // (e.g. a concurrent doc-switch removed the source tab before the
  // assistant message landed) this function silently returned `prev`,
  // discarding the entire assistant turn. Now we fall back to a recreated
  // tab keyed off the assistant message's `slideIndex` (and `slideId` from
  // the modifications, if available). This keeps the assistant message in
  // the chat history rather than disappearing it.
  if (!activeTab) {
    const fallbackSlideIndex = assistantMessage.slideIndex ?? affectedSlides[0]?.index ?? 0;
    const fallbackSlideId =
      affectedSlides.find((slide) => slide.index === fallbackSlideIndex)?.id ||
      affectedSlides[0]?.id ||
      "";
    const fallbackSlideNumber =
      assistantMessage.slideNumber ??
      affectedSlides.find((slide) => slide.index === fallbackSlideIndex)?.slideNumber ??
      affectedSlides[0]?.slideNumber ??
      fallbackSlideIndex + 1;
    // Try to find an existing tab matching the fallback slide first so we
    // don't create a duplicate when the source tab was just renamed/recreated.
    const existingTabForFallback = nextTabs.find((tab) => {
      if (fallbackSlideId && tab.session.slideId === fallbackSlideId) return true;
      return tab.session.slideIndex === fallbackSlideIndex;
    });
    if (existingTabForFallback) {
      console.warn(
        "[CHAT] mirrorAssistantMessageToAffectedSlides: source tab " +
          `"${activeTabId}" missing — falling back to existing tab ` +
          `"${existingTabForFallback.id}" matching slide ${fallbackSlideNumber}.`
      );
      activeTab = existingTabForFallback;
      activeTabId = existingTabForFallback.id;
    } else {
      console.warn(
        "[CHAT] mirrorAssistantMessageToAffectedSlides: source tab " +
          `"${activeTabId}" missing and no matching slide tab — creating ` +
          `recovery tab for slide ${fallbackSlideNumber}.`
      );
      const recoveryTab = createTab({
        id: fallbackSlideId,
        index: fallbackSlideIndex,
        slideNumber: fallbackSlideNumber,
      });
      nextTabs = [...nextTabs, recoveryTab];
      activeTab = recoveryTab;
      activeTabId = recoveryTab.id;
    }
  }

  const targetTabs = new Map<string, SlideSessionRef>();
  targetTabs.set(activeTabId, {
    id: activeTab.session.slideId,
    index: activeTab.session.slideIndex,
    slideNumber: activeTab.session.slideNumber,
  });

  for (const affected of affectedSlides) {
    const existingTab = nextTabs.find((tab) => {
      if (affected.id) {
        if (!tab.session.slideId) {
          return false;
        }
        return tab.session.slideId === affected.id;
      }
      return tab.session.slideIndex === affected.index;
    });
    if (existingTab) {
      targetTabs.set(existingTab.id, {
        id: affected.id || existingTab.session.slideId,
        index: affected.index,
        slideNumber: affected.slideNumber ?? existingTab.session.slideNumber,
      });
      continue;
    }

    const created = createTab({
      id: affected.id || "",
      index: affected.index,
      slideNumber: affected.slideNumber,
    });
    nextTabs = [...nextTabs, created];
    targetTabs.set(created.id, {
      id: affected.id,
      index: affected.index,
      slideNumber: affected.slideNumber,
    });
  }

  const timestamp = Date.now();
  const updatedTabs = nextTabs.map((tab) => {
    const target = targetTabs.get(tab.id);
    if (!target) {
      return tab;
    }

    const isSourceTab = tab.id === activeTabId;
    const messageForTab = isSourceTab
      ? assistantMessage
      : {
          ...stripPerSlideCitationPayload(assistantMessage),
          id: generateId(),
          timestamp,
          slideIndex: target.index,
          slideNumber: target.slideNumber ?? target.index + 1,
        };
    const alreadyHasAssistantMessage = tab.session.messages.some(
      (message) => message.id === messageForTab.id
    );
    // De-dupe by source user message id (handles retries calling mirror
    // again with the same userMessage).
    const shouldMirrorUserMessage =
      !isSourceTab &&
      userMessage !== undefined &&
      tab.session.messages[tab.session.messages.length - 1]?.id !== userMessage.id;
    const mirroredUserMessage = shouldMirrorUserMessage
      ? {
          ...userMessage!,
          id: generateId(),
          timestamp,
          slideIndex: target.index,
          slideNumber: target.slideNumber ?? target.index + 1,
        }
      : undefined;

    const nextMessages = alreadyHasAssistantMessage
      ? mirroredUserMessage
        ? [...tab.session.messages, mirroredUserMessage]
        : tab.session.messages
      : mirroredUserMessage
        ? [...tab.session.messages, mirroredUserMessage, messageForTab]
        : [...tab.session.messages, messageForTab];

    return {
      ...tab,
      session: {
        ...tab.session,
        slideId: target.id || tab.session.slideId,
        slideIndex: target.index,
        slideNumber: target.slideNumber ?? target.index + 1,
        name: `Slide ${target.slideNumber ?? target.index + 1}`,
        messages: nextMessages,
        updatedAt: timestamp,
      },
    };
  });

  return {
    ...prev,
    tabs: updatedTabs,
  };
}

/**
 * Surgically patches the assistant `ChatMessage.content` field for a
 * given message id across all tabs that received the pre-apply mirror.
 *
 * Used after the write flow finishes when the apply outcome (failure /
 * no-op) means the original empty placeholder content should be
 * replaced with the user-facing apply status message. This is a
 * narrow, idempotent update — we never touch other fields and we
 * bump `session.updatedAt` so the `isStorageTabsStateFresher` gate
 * continues to recognise the post-apply state as the freshest write.
 */
export function updateAssistantMessageContent(
  prev: TabsState,
  messageId: string,
  newContent: string,
  options: { clearModifications?: boolean } = {}
): TabsState {
  const timestamp = Date.now();
  const clearModifications = options.clearModifications === true;
  let didUpdate = false;
  const nextTabs = prev.tabs.map((tab) => {
    const idx = tab.session.messages.findIndex((m) => m.id === messageId && m.role === "assistant");
    if (idx < 0) return tab;
    const existing = tab.session.messages[idx];
    const needsContentUpdate = existing.content !== newContent;
    const needsModsCleared = clearModifications && existing.modifications !== undefined;
    if (!needsContentUpdate && !needsModsCleared) return tab;
    didUpdate = true;
    const nextMessages = [...tab.session.messages];
    const nextMessage: ChatMessage = { ...existing, content: newContent };
    if (needsModsCleared) {
      // Apply truly failed (zero successful ops). Strip the model's
      // slide intents so `ChatHistory.AppliedChangesRow` doesn't render a
      // success-looking card next to the failure bubble. Keep the model's
      // declared citation payload intact.
      nextMessage.modifications = { ...existing.modifications!, slides: [] };
    }
    nextMessages[idx] = nextMessage;
    return {
      ...tab,
      session: {
        ...tab.session,
        messages: nextMessages,
        updatedAt: timestamp,
      },
    };
  });
  return didUpdate ? { ...prev, tabs: nextTabs } : prev;
}

/**
 * Per-turn citation accumulator. The turn's tool calls (e.g.
 * `finster_query`) deliver `Citation[]` chunks; we merge them into a
 * single de-duplicated list keyed by every uuid we can pull out of
 * each citation. Used by `handleQuery` and exported here for direct
 * unit testing.
 */
export interface TurnCitationCollector {
  citations: Citation[];
  citationIdToIndex: Map<string, number>;
  /**
   * UUID → display value declared by the model on
   * `modifications.citations[]`. The model is instructed (via the
   * top-level `citations` schema field) to emit only the entries
   * whose underlying value was actually placed on the slide this
   * turn — so this map is precisely the "relevant to slide" set
   * used to filter Retrieved-sources / Citations cards. First
   * non-empty value wins.
   */
  citedValuesByUuid: Map<string, string>;
}

export function createTurnCitationCollector(): TurnCitationCollector {
  return {
    citations: [],
    citationIdToIndex: new Map<string, number>(),
    citedValuesByUuid: new Map<string, string>(),
  };
}

export function mergeCitationsIntoTurn(
  collector: TurnCitationCollector,
  incoming: Citation[] | undefined
): void {
  if (!Array.isArray(incoming) || incoming.length === 0) return;
  for (const citation of incoming) {
    if (!citation || typeof citation !== "object") continue;
    const ids = extractIdsFromCitation(citation);
    if (ids.length === 0) {
      collector.citations.push(citation);
      continue;
    }
    let alreadyKnown = true;
    for (let i = 0; i < ids.length; i++) {
      if (!collector.citationIdToIndex.has(ids[i])) {
        alreadyKnown = false;
        break;
      }
    }
    if (alreadyKnown) continue;
    const index = collector.citations.push(citation) - 1;
    for (let i = 0; i < ids.length; i++) {
      const id = ids[i];
      if (!collector.citationIdToIndex.has(id)) collector.citationIdToIndex.set(id, index);
    }
  }
}

/**
 * Ingest the model's declared citation section
 * (`modifications.citations[]`) into the turn collector. The model
 * is prompted (via the schema's top-level `citations` field) to emit
 * exactly the citations whose underlying value was placed on the
 * slide this turn — so each entry's `id` becomes the canonical
 * "cited UUID" and `value` is the displayed value the
 * Retrieved-sources cards surface.
 *
 * Tolerant of:
 *  - missing/empty `citations` (turns without finster_query data —
 *    map stays empty, cards stay hidden);
 *  - duplicate ids (first non-empty value wins so later truncated
 *    re-mentions can't clobber the first full one);
 *  - malformed entries (entries without a string `id` are dropped).
 */
export function mergeDeclaredCitations(
  collector: TurnCitationCollector,
  citations: ModificationCitation[] | undefined | null
): void {
  if (!Array.isArray(citations) || citations.length === 0) return;
  for (const entry of citations) {
    if (!entry || typeof entry !== "object") continue;
    const id = typeof entry.id === "string" ? entry.id.trim() : "";
    if (!id) continue;
    const value = typeof entry.value === "string" ? entry.value.trim() : "";
    if (!value) continue;
    if (!collector.citedValuesByUuid.has(id)) {
      collector.citedValuesByUuid.set(id, value);
    }
  }
}

export interface AppliedCitationsToolPayload {
  /** Real-id-keyed per-slide citation bindings from the apply pipeline manifest. */
  bindings?: SlideCitationBinding[];
  citations?: ModificationCitation[];
  citedValuesByUuid?: Record<string, string>;
}

export function mergeAppliedCitationsToolPayload(
  collector: TurnCitationCollector,
  citationBindings: SlideCitationBinding[],
  payload: AppliedCitationsToolPayload | undefined | null
): void {
  if (!payload || typeof payload !== "object") return;
  mergeDeclaredCitations(collector, payload.citations);
  const directValues = payload.citedValuesByUuid;
  if (directValues && typeof directValues === "object") {
    for (const [id, value] of Object.entries(directValues)) {
      if (!id || typeof value !== "string" || value.trim().length === 0) continue;
      if (!collector.citedValuesByUuid.has(id)) {
        collector.citedValuesByUuid.set(id, value.trim());
      }
    }
  }
  if (Array.isArray(payload.bindings) && payload.bindings.length > 0) {
    citationBindings.push(...payload.bindings);
  }
}

/**
 * Merge two slide-ref lists keyed by host id, with `overrides` winning. Used to
 * layer the slides created during a turn on top of the turn-start snapshot so
 * citation resolution can find slides that did not exist when the turn began.
 */
export function mergeSlideRefsById(
  base: SlideSessionRef[],
  overrides: SlideSessionRef[]
): SlideSessionRef[] {
  const byId = new Map<string, SlideSessionRef>();
  for (const ref of base) {
    if (ref && typeof ref.id === "string" && ref.id.length > 0) byId.set(ref.id, ref);
  }
  for (const ref of overrides) {
    if (ref && typeof ref.id === "string" && ref.id.length > 0) byId.set(ref.id, ref);
  }
  return Array.from(byId.values());
}

/**
 * Compact the per-turn collector into the shape persisted on
 * `ChatMessage` (`citations` array + `citationsById` index +
 * `citedValuesByUuid`). New turns that retrieved sources persist an
 * empty cited-value map when the model declared no citations; the UI
 * treats that as "show no retrieved source cards" rather than falling
 * back to the legacy unfiltered list.
 */
export function compactTurnCitations(collector: TurnCitationCollector): {
  citations?: Citation[];
  citationsById?: Record<string, number>;
  citedValuesByUuid?: Record<string, string>;
} {
  if (collector.citations.length === 0 && collector.citedValuesByUuid.size === 0) return {};
  const compactedCitationsById: Record<string, number> = {};
  collector.citationIdToIndex.forEach((idx, id) => {
    compactedCitationsById[id] = idx;
  });
  const compactedCitedValues: Record<string, string> = {};
  collector.citedValuesByUuid.forEach((value, id) => {
    compactedCitedValues[id] = value;
  });
  return {
    citations: collector.citations.length ? collector.citations.slice() : undefined,
    citationsById: collector.citations.length ? compactedCitationsById : undefined,
    citedValuesByUuid:
      collector.citations.length || collector.citedValuesByUuid.size
        ? compactedCitedValues
        : undefined,
  };
}

function collectAffectedTabIds(
  state: TabsState,
  activeTabId: string,
  affectedSlides: SlideSessionRef[]
): string[] {
  const ids = new Set<string>([activeTabId]);
  for (const affected of affectedSlides) {
    const matchedTab = state.tabs.find((tab) => {
      if (affected.id) {
        return tab.session.slideId === affected.id;
      }
      return tab.session.slideIndex === affected.index;
    });
    if (matchedTab) {
      ids.add(matchedTab.id);
    }
  }
  return Array.from(ids);
}

export const useQueryHandler = ({
  activeTab,
  updateActiveTab,
  setTabsState,
  syncTabsWithPresentation,
  taskContext,
  aiMode,
  log,
  brandId,
  plannerMode = false,
  companyId,
  accountScopeKey,
  pendingDeckPlan,
  onPresentDeckPlan,
  onUpdateDeckPlan,
  getPendingDeckPlan,
  isReviewSessionOpen,
  onReviewPlanQueryStarted,
  onReviewPlanningEvidence,
  onReviewOperationActivity,
  onReviewSkillContextResolved,
  onSkillSubmission,
  onSkillDecision,
  onDismissSkillDecision,
}: UseQueryHandlerProps) => {
  const [resultsByTabId, setResultsByTabId] = useState<Record<string, QueryResult>>({});
  const [pendingFixQuery, setPendingFixQuery] = useState<string | null>(null);
  const [cameFromReview, setCameFromReview] = useState(false);

  const logRef = useRef(log);
  useEffect(() => {
    logRef.current = log;
  });
  const accountScopeKeyRef = useRef(accountScopeKey ?? null);
  accountScopeKeyRef.current = accountScopeKey ?? null;
  const onPresentDeckPlanRef = useRef(onPresentDeckPlan);
  onPresentDeckPlanRef.current = onPresentDeckPlan;
  const onReviewPlanQueryStartedRef = useRef(onReviewPlanQueryStarted);
  onReviewPlanQueryStartedRef.current = onReviewPlanQueryStarted;
  const onUpdateDeckPlanRef = useRef(onUpdateDeckPlan);
  onUpdateDeckPlanRef.current = onUpdateDeckPlan;
  const getPendingDeckPlanRef = useRef(getPendingDeckPlan);
  getPendingDeckPlanRef.current = getPendingDeckPlan;
  const isReviewSessionOpenRef = useRef(isReviewSessionOpen);
  isReviewSessionOpenRef.current = isReviewSessionOpen;
  const onReviewPlanningEvidenceRef = useRef(onReviewPlanningEvidence);
  onReviewPlanningEvidenceRef.current = onReviewPlanningEvidence;
  const onReviewOperationActivityRef = useRef(onReviewOperationActivity);
  onReviewOperationActivityRef.current = onReviewOperationActivity;
  const onReviewSkillContextResolvedRef = useRef(onReviewSkillContextResolved);
  onReviewSkillContextResolvedRef.current = onReviewSkillContextResolved;
  const onSkillSubmissionRef = useRef(onSkillSubmission);
  onSkillSubmissionRef.current = onSkillSubmission;
  const onSkillDecisionRef = useRef(onSkillDecision);
  onSkillDecisionRef.current = onSkillDecision;
  const onDismissSkillDecisionRef = useRef(onDismissSkillDecision);
  onDismissSkillDecisionRef.current = onDismissSkillDecision;
  const activeQueryAbortRef = useRef<ActiveQueryAbortRegistry>({});
  const skillResolveSingleFlightRef = useRef<ReturnType<
    typeof createSkillResolveSingleFlight
  > | null>(null);
  if (!skillResolveSingleFlightRef.current) {
    skillResolveSingleFlightRef.current = createSkillResolveSingleFlight();
  }
  const reviewSkillBindingRef = useRef<ReviewSkillBinding | null>(null);
  const reviewOperationGenerationsRef = useRef<Map<string, number>>(new Map());
  useEffect(() => {
    if (!pendingDeckPlan) {
      reviewSkillBindingRef.current = null;
      const activeReviewOperationIds = new Set(
        Object.values(activeQueryAbortRef.current)
          .map((activeQuery) => activeQuery?.reviewOperationId)
          .filter((value): value is string => Boolean(value))
      );
      for (const reviewOperationId of reviewOperationGenerationsRef.current.keys()) {
        if (!activeReviewOperationIds.has(reviewOperationId)) {
          reviewOperationGenerationsRef.current.delete(reviewOperationId);
        }
      }
      return;
    }
    let transientContext: DeckPlanTransientContext | undefined;
    try {
      transientContext = getDeckPlanTransientContext(pendingDeckPlan);
    } catch (error) {
      reviewSkillBindingRef.current = null;
      logRef.current(
        `Invalid Review Skill context: ${error instanceof Error ? error.message : String(error)}`
      );
      return;
    }
    if (transientContext) {
      reviewSkillBindingRef.current = {
        reviewOperationId: transientContext.reviewOperationId,
        state: transientContext.reviewSkillBindingState,
        context: transientContext.skillRunContext,
      };
      reviewOperationGenerationsRef.current.set(
        transientContext.reviewOperationId,
        Math.max(
          reviewOperationGenerationsRef.current.get(transientContext.reviewOperationId) ?? 0,
          transientContext.reviewOperationGeneration
        )
      );
    } else if (!reviewSkillBindingRef.current && pendingDeckPlan.skillRunContext) {
      reviewSkillBindingRef.current = {
        state: "bound",
        context: pendingDeckPlan.skillRunContext,
      };
    }
  }, [pendingDeckPlan]);
  useEffect(
    () => () => {
      abortAllActiveQueries(activeQueryAbortRef.current);
      skillResolveSingleFlightRef.current?.clear();
      reviewOperationGenerationsRef.current.clear();
    },
    []
  );
  const result = activeTab ? resultsByTabId[activeTab.id] || null : null;
  const setResult = useCallback(
    (nextResult: QueryResult | null) => {
      if (!activeTab) return;
      setResultsByTabId((prev) => {
        if (nextResult === null) {
          if (!(activeTab.id in prev)) return prev;
          const next = { ...prev };
          delete next[activeTab.id];
          return next;
        }
        return { ...prev, [activeTab.id]: nextResult };
      });
    },
    [activeTab]
  );

  const cancelAllQueries = useCallback(() => {
    const cancelledTabIds = abortAllActiveQueries(activeQueryAbortRef.current);
    skillResolveSingleFlightRef.current?.clear();
    reviewOperationGenerationsRef.current.clear();
    setResultsByTabId({});
    setPendingFixQuery(null);
    setCameFromReview(false);
    if (cancelledTabIds.length === 0) return;

    const cancelledTabs = new Set(cancelledTabIds);
    logRef.current(
      `Cancelled ${cancelledTabs.size} active chat quer${cancelledTabs.size === 1 ? "y" : "ies"}`
    );
    setTabsState((prev) =>
      cancelledTabIds.reduce((next, tabId) => clearQueryLoadingStateForTab(next, tabId), prev)
    );
  }, [setTabsState]);

  const releaseActiveQuery = useCallback((tabId: string): boolean => {
    const activeQuery = activeQueryAbortRef.current[tabId];
    if (!activeQuery) return false;
    activeQuery.controller.abort();
    activeQuery.settleReviewOperationActivity?.();
    delete activeQueryAbortRef.current[tabId];
    return true;
  }, []);

  const cancelQuery = useCallback(() => {
    if (!activeTab || !releaseActiveQuery(activeTab.id)) return;

    logRef.current("Chat query cancelled by user");
    setTabsState((prev) => clearQueryLoadingStateForTab(prev, activeTab.id));
    setResultsByTabId((prev) => {
      if (!(activeTab.id in prev)) return prev;
      const next = { ...prev };
      delete next[activeTab.id];
      return next;
    });
    onDismissSkillDecisionRef.current?.();
  }, [activeTab, releaseActiveQuery, setTabsState]);

  const removeLastUserMessage = useCallback(
    (tabId: string) => {
      setTabsState((prev) => {
        const currentTab = prev.tabs.find((tab) => tab.id === tabId);
        if (!currentTab) return prev;
        const messages = messagesBeforeLastUser(currentTab.session.messages);
        if (!messages) {
          return clearQueryLoadingStateForTab(prev, tabId);
        }
        return updateTab(prev, tabId, {
          ...clearedQueryLoadingState,
          session: {
            ...currentTab.session,
            messages,
          },
        });
      });
      setResultsByTabId((prev) => {
        if (!(tabId in prev)) return prev;
        const next = { ...prev };
        delete next[tabId];
        return next;
      });
    },
    [setTabsState]
  );

  const handleQueryRef = useRef<
    (
      query: string,
      dataSources?: readonly FinsterQueryDataSource[],
      userFileIds?: readonly string[],
      skillSubmission?: SkillSubmissionInput
    ) => Promise<boolean | void>
  >(async () => false);

  const handleReplaceSkillRun = useCallback(
    async (input: {
      prompt: string;
      selectedSkills: readonly SelectedSkillReference[];
      dataSources?: readonly FinsterQueryDataSource[];
      userFileIds?: readonly string[];
    }): Promise<boolean> => {
      if (!activeTab) {
        throw new Error(SKILL_RUN_ERRORS.couldNotStop);
      }
      const tabId = activeTab.id;
      return replaceSkillRun({
        stop: () => {
          releaseActiveQuery(tabId);
        },
        removeLastUserMessage: () => {
          removeLastUserMessage(tabId);
        },
        resubmit: async () => {
          const accepted = await handleQueryRef.current(
            input.prompt,
            input.dataSources,
            input.userFileIds,
            {
              selectedSkills: [...input.selectedSkills],
              selectionProvided: true,
            }
          );
          return accepted !== false;
        },
      });
    },
    [activeTab, releaseActiveQuery, removeLastUserMessage]
  );

  const handleQuery = useCallback(
    async (
      query: string,
      dataSources?: readonly FinsterQueryDataSource[],
      userFileIds?: readonly string[],
      skillSubmission?: SkillSubmissionInput
    ) => {
      if (!activeTab) return false;
      const chatHistoryForModel = snapshotCompletedChatHistoryForRun(activeTab.session.messages);
      const log = (message: string) => logRef.current(message);
      const sourceTabId = activeTab.id;
      const sourceAccountScopeKey = accountScopeKey ?? null;
      const submissionId = createSkillSubmissionId();
      const ownsSubmissionAccount = () => accountScopeKeyRef.current === sourceAccountScopeKey;
      // Both chart-routing flags are captured once, together, so a ConfigCat
      // refresh mid-turn cannot change the default chart route after the model
      // has already been told which routes exist.
      const chartRoutingGate = snapshotPowerPointChartRoutingGate();
      const allowActiveGraph = chartRoutingGate.activeGraphEnabled;
      const skillSubmissionSnapshot = snapshotSkillSubmissionOptions(skillSubmission);
      const researchScope = snapshotRunResearchSelection({
        dataSources: dataSources ?? [],
        userFileIds,
      });
      const reviewSessionIsOpen = isReviewSessionOpenRef.current
        ? Boolean(isReviewSessionOpenRef.current())
        : Boolean(pendingDeckPlan);
      const reviewPlanAtSubmit =
        pendingDeckPlan && reviewSessionIsOpen ? pendingDeckPlan : undefined;
      const failBeforeQueryStart = (error: unknown): false => {
        const errorMessage = error instanceof Error ? error.message : String(error);
        log(`Chat setup failed: ${errorMessage}`);
        setResult({
          success: false,
          message: toUserFriendlyRequestError(errorMessage),
        });
        return false;
      };
      let pendingReviewContext: DeckPlanTransientContext | undefined;
      try {
        pendingReviewContext = getDeckPlanTransientContext(reviewPlanAtSubmit);
      } catch (error) {
        return failBeforeQueryStart(error);
      }
      const pendingReviewOperationId =
        pendingReviewContext?.reviewOperationId ?? reviewPlanAtSubmit?.reviewOperationId;
      const currentReviewBinding =
        reviewPlanAtSubmit &&
        reviewSkillBindingRef.current &&
        (!pendingReviewOperationId ||
          !reviewSkillBindingRef.current.reviewOperationId ||
          reviewSkillBindingRef.current.reviewOperationId === pendingReviewOperationId)
          ? reviewSkillBindingRef.current
          : null;
      let reviewSkillBinding: ReviewSkillBinding =
        currentReviewBinding ??
        (pendingReviewContext
          ? {
              reviewOperationId: pendingReviewContext.reviewOperationId,
              state: pendingReviewContext.reviewSkillBindingState,
              context: pendingReviewContext.skillRunContext,
            }
          : {
              state: reviewPlanAtSubmit?.skillRunContext ? "bound" : "unbound",
              context: reviewPlanAtSubmit?.skillRunContext,
            });
      let inheritedSkillRunContext: SkillRunContext | undefined;
      try {
        inheritedSkillRunContext = resolveInheritedReviewSkillContext({
          pendingDeckPlan: reviewPlanAtSubmit,
          currentBinding: currentReviewBinding,
          selectionProvided: skillSubmissionSnapshot.selectionProvided,
        });
      } catch (error) {
        return failBeforeQueryStart(error);
      }
      const hasInheritedReviewSkillBinding =
        Boolean(reviewPlanAtSubmit) &&
        shouldInheritReviewSkillBinding(
          reviewSkillBinding,
          skillSubmissionSnapshot.selectionProvided
        );
      const mintedNewReviewOperation = !pendingReviewOperationId;
      const reviewOperationId = pendingReviewOperationId ?? createReviewOperationId();
      if (mintedNewReviewOperation) {
        onReviewPlanQueryStartedRef.current?.(reviewOperationId);
      }
      const previousReviewOperationGeneration = Math.max(
        pendingReviewContext?.reviewOperationGeneration ??
          reviewPlanAtSubmit?.reviewOperationGeneration ??
          0,
        reviewOperationGenerationsRef.current.get(reviewOperationId) ?? 0
      );
      const reviewOperationGeneration = previousReviewOperationGeneration + 1;
      reviewOperationGenerationsRef.current.set(reviewOperationId, reviewOperationGeneration);
      reviewSkillBinding = {
        ...reviewSkillBinding,
        reviewOperationId,
      };
      const isCurrentReviewGeneration = () =>
        reviewOperationGenerationsRef.current.get(reviewOperationId) === reviewOperationGeneration;
      let planningEvidence = reviewPlanAtSubmit?.planningEvidence;
      let skillRunContext: SkillRunContext | undefined;
      let reviewEvidenceBound = Boolean(reviewPlanAtSubmit);
      const releaseUnusedReviewGeneration = () => {
        const reviewStillOpen = isReviewSessionOpenRef.current
          ? Boolean(isReviewSessionOpenRef.current())
          : Boolean(reviewEvidenceBound || reviewPlanAtSubmit);
        if (reviewStillOpen && (reviewEvidenceBound || reviewPlanAtSubmit)) return;
        if (
          reviewOperationGenerationsRef.current.get(reviewOperationId) === reviewOperationGeneration
        ) {
          reviewOperationGenerationsRef.current.delete(reviewOperationId);
        }
      };
      let activityRegistered = false;
      let activitySettled = false;
      let settleReviewOperationActivity: () => void = () => {};
      const reviewOperationActivity = new Promise<void>((resolve) => {
        settleReviewOperationActivity = () => {
          if (activitySettled) return;
          activitySettled = true;
          resolve();
        };
      });
      const registerReviewOperationActivity = (): boolean => {
        if (activityRegistered) {
          return true;
        }
        const accepted =
          onReviewOperationActivityRef.current?.(
            reviewOperationId,
            reviewOperationGeneration,
            reviewOperationActivity
          ) !== false;
        if (!accepted) {
          settleReviewOperationActivity();
          return false;
        }
        activityRegistered = true;
        return true;
      };

      const bindTransientReviewContext = (plan: DeckPlan): DeckPlan => {
        if (planningEvidence) {
          planningEvidence = withPlanningEvidenceSkillBinding(
            planningEvidence,
            reviewSkillBinding.context?.skills?.map((skill) => skill.id),
            reviewSkillBinding.context?.guidanceChars
          );
        }
        return reattachDeckPlanTransientContext(stripDeckPlanTransientContext(plan), {
          reviewOperationId,
          reviewOperationGeneration,
          reviewSkillBindingState: reviewSkillBinding.state,
          ...(reviewSkillBinding.context
            ? { skillRunContext: reviewSkillBinding.context }
            : undefined),
          ...(planningEvidence ? { planningEvidence } : undefined),
        });
      };
      let skillBootstrapPromise: Promise<SkillTurnBootstrapResult> | null = null;
      let skillBootstrapSettled = false;
      let skillResolveSettledAt: number | undefined;
      const cancelledTabIds = reviewPlanAtSubmit
        ? abortActiveQueriesForReviewOperation(activeQueryAbortRef.current, reviewOperationId)
        : [];
      const previousActiveQuery = activeQueryAbortRef.current[sourceTabId];
      if (previousActiveQuery) {
        previousActiveQuery.controller.abort();
        previousActiveQuery.settleReviewOperationActivity?.();
        delete activeQueryAbortRef.current[sourceTabId];
        cancelledTabIds.push(previousActiveQuery.tabId);
      }
      if (cancelledTabIds.length > 0) {
        const cancelledTabs = new Set(cancelledTabIds);
        setTabsState((prev) =>
          Array.from(cancelledTabs).reduce(
            (next, tabId) => clearQueryLoadingStateForTab(next, tabId),
            prev
          )
        );
        setResultsByTabId((prev) => {
          const next = { ...prev };
          let changed = false;
          for (const tabId of cancelledTabs) {
            if (!(tabId in next)) continue;
            delete next[tabId];
            changed = true;
          }
          return changed ? next : prev;
        });
      }
      const abortController = new AbortController();
      const abortSignal = abortController.signal;
      const abortPendingSetup = () => {
        if (!skillBootstrapSettled) {
          abortController.abort();
        }
      };
      activeQueryAbortRef.current[sourceTabId] = {
        tabId: sourceTabId,
        controller: abortController,
        reviewOperationId,
        reviewOperationGeneration,
        settleReviewOperationActivity,
      };
      if (reviewPlanAtSubmit && !registerReviewOperationActivity()) {
        abortController.abort();
        delete activeQueryAbortRef.current[sourceTabId];
        if (
          reviewOperationGenerationsRef.current.get(reviewOperationId) === reviewOperationGeneration
        ) {
          reviewOperationGenerationsRef.current.delete(reviewOperationId);
        }
        return failBeforeQueryStart(
          new Error(
            "The pending Review state could not be submitted. Please reopen Review and try again."
          )
        );
      }
      const analyticsStartedAt = Date.now();
      trackQuerySubmitted({ mode: aiMode, hasContext: !!taskContext });
      let queryRunOutcomeTracked = false;
      let skillResolveStartedAt: number | undefined;
      let skillMetadata: SkillTurnMetadata | undefined;
      let skillAnalytics: ReturnType<typeof buildQuerySkillsAnalytics>;
      const getOutcomeSkillAnalytics = () => {
        if (!skillAnalytics || !skillRunContext || !reviewEvidenceBound || !planningEvidence) {
          return skillAnalytics;
        }
        return {
          ...skillAnalytics,
          skillPlanningEvidenceCount: planningEvidence.count,
          skillPlanningEvidenceCitationCount: planningEvidence.citationCount,
          skillPlanningEvidenceChars: planningEvidence.totalAnswerChars,
          skillPlanningEvidenceSizeBytes: planningEvidence.sizeBytes,
          skillPlanningEvidenceTruncated: planningEvidence.truncated,
        };
      };
      const postSkillOutcomeObservation = (
        status: "completed" | "failed" | "cancelled",
        skills: ReturnType<typeof getOutcomeSkillAnalytics>
      ) => {
        if (!skills) return;
        postPptxLangfuseObservation(langfuseRunContext, {
          name: "pptx.skills.outcome",
          metadata: { ...skills, status },
          ...(status === "failed" ? { level: "ERROR" as const } : undefined),
          endTime: new Date().toISOString(),
        });
      };
      const langfuseRunContext = createPptxLangfuseRunContext({
        mode: aiMode,
        hasContext: !!taskContext,
        sessionId: activeTab.session.id || sourceTabId,
      });
      // Start tracing concurrently, but do not await it until after the
      // synchronous user echo, spinner, and thinking ticket are registered.
      const langfuseRunStart = startPptxLangfuseRun(langfuseRunContext, { query });
      const trackQuerySuccess = () => {
        if (queryRunOutcomeTracked) return;
        queryRunOutcomeTracked = true;
        const outcomeSkills = getOutcomeSkillAnalytics();
        trackQueryRun({
          mode: aiMode,
          hasContext: !!taskContext,
          durationMs: Date.now() - analyticsStartedAt,
          status: "completed",
        });
        postSkillOutcomeObservation("completed", outcomeSkills);
        postPptxLangfuseOutcome(langfuseRunContext, {
          status: "completed",
          durationMs: Date.now() - analyticsStartedAt,
        });
      };
      const trackQueryFailure = (errorType: string) => {
        if (queryRunOutcomeTracked) return;
        queryRunOutcomeTracked = true;
        const outcomeStatus = errorType === "cancelled" ? "cancelled" : "failed";
        const outcomeSkills = getOutcomeSkillAnalytics();
        trackQueryRun({
          mode: aiMode,
          hasContext: !!taskContext,
          durationMs: Date.now() - analyticsStartedAt,
          status: outcomeStatus,
          errorType,
        });
        postSkillOutcomeObservation(outcomeStatus, outcomeSkills);
        postPptxLangfuseOutcome(langfuseRunContext, {
          status: outcomeStatus,
          durationMs: Date.now() - analyticsStartedAt,
          errorType,
        });
      };

      let thinkingTicket: ReturnType<typeof registerThinkingPhase> | null = null;
      const unregisterThinkingTicket = () => {
        if (!thinkingTicket) return;
        unregisterThinkingPhase(thinkingTicket);
        thinkingTicket = null;
      };
      const failAfterQueryAccepted = (error: unknown) => {
        abortPendingSetup();
        settleReviewOperationActivity();
        skillResolveSingleFlightRef.current?.release(submissionId);
        const errorMessage = error instanceof Error ? error.message : String(error);
        if (abortSignal.aborted) {
          trackQueryFailure("cancelled");
          unregisterThinkingTicket();
          const ownsSetupLoadingState = queryOwnsLoadingState(
            activeQueryAbortRef.current,
            sourceTabId,
            abortController
          );
          if (ownsSetupLoadingState) {
            updateActiveTab({
              isLoading: false,
              loadingStatus: "",
              streamingThinking: "",
              thoughtProcessCollapseSignal: undefined,
              refinementStep: 0,
              visionFeedback: "",
            });
            setResult(null);
          }
          if (activeQueryAbortRef.current[sourceTabId]?.controller === abortController) {
            delete activeQueryAbortRef.current[sourceTabId];
          }
          releaseUnusedReviewGeneration();
          return;
        }
        log(`Chat setup failed: ${errorMessage}`);
        trackQueryFailure("setup_error");
        unregisterThinkingTicket();
        const ownsSetupLoadingState = queryOwnsLoadingState(
          activeQueryAbortRef.current,
          sourceTabId,
          abortController
        );
        if (ownsSetupLoadingState) {
          updateActiveTab({
            isLoading: false,
            loadingStatus: "",
            streamingThinking: "",
            thoughtProcessCollapseSignal: undefined,
            refinementStep: 0,
            visionFeedback: "",
          });
          setResult({
            success: false,
            message: toUserFriendlyRequestError(errorMessage),
          });
        }
        if (activeQueryAbortRef.current[sourceTabId]?.controller === abortController) {
          delete activeQueryAbortRef.current[sourceTabId];
        }
        releaseUnusedReviewGeneration();
      };

      try {
        // ── IMMEDIATE SPINNER (synchronous, before any await) ──────────────────
        // The FactSet round-trip in `capturePresentationKey` below can take a
        // few hundred ms. Without this synchronous `updateActiveTab` the user
        // clicks Send, sees the input clear, and then sees NOTHING for that
        // window — no spinner, no message — which reads as "the submission
        // was dropped". Setting `isLoading=true` here gives the chat header
        // an immediate "Thinking..." state. The user-message echo is now raised
        // SYNCHRONOUSLY just below (no longer deferred to `capturePresentationKey`),
        // while all SUBSEQUENT writes still route via `applyToSourceTabs` so they
        // land in the correct presentation's storage envelope on a mid-flight switch.
        // Echo the user's message AND raise the spinner in ONE synchronous state
        // update, before the `capturePresentationKey` round-trip - so the bubble
        // and "Thinking..." appear together in the same render. documentId is
        // known synchronously here; persist eagerly under it so the echo survives
        // a same-frame active-id flip (matches `applyToSourceTabs` below).
        const sourceDocumentId = getDocumentId();
        const userMessageTimestamp = Date.now();
        // Echo with the composer's Skill chips already resolved so the bubble
        // never paints raw `/slug` and then remorphs into a pill on send.
        skillMetadata = createPendingSkillTurnMetadata(skillSubmissionSnapshot.selectedSkills);
        let userMessage: ChatMessage = {
          id: generateId(),
          role: "user",
          content: taskContext ? `📋 Using context: ${taskContext.name}\n\n${query}` : query,
          timestamp: userMessageTimestamp,
          slideIndex: activeTab.session.slideIndex,
          slideNumber: activeTab.session.slideNumber ?? activeTab.session.slideIndex + 1,
          ...(skillMetadata ? { skillMetadata } : {}),
        };
        setTabsState((prev) => {
          const currentTab = prev.tabs.find((t) => t.id === sourceTabId);
          if (!currentTab) return prev;
          const next = updateTab(
            prev,
            sourceTabId,
            buildQueryStartTabUpdate(currentTab, userMessage, userMessageTimestamp)
          );
          if (next !== prev) {
            try {
              saveTabsStateForDocumentId(sourceDocumentId, next, sourceAccountScopeKey);
            } catch {
              // Storage layer already logs; never break React's commit.
            }
          }
          return next;
        });
        setResult(null);
        skillResolveStartedAt = Date.now();
        skillBootstrapPromise = skillResolveSingleFlightRef.current!.resolveSkillsOnce({
          submissionId,
          userQuery: query,
          dataSources: dataSources === undefined ? undefined : researchScope.dataSources,
          userFileIds: researchScope.userFileIds,
          userFileTags: [],
          selection: skillSubmissionSnapshot,
          inheritedContext: inheritedSkillRunContext,
          hasInheritedContext: hasInheritedReviewSkillBinding,
          signal: abortSignal,
        });
        onSkillSubmissionRef.current?.(submissionId);
        void skillBootstrapPromise.then(
          () => {
            skillBootstrapSettled = true;
            skillResolveSettledAt = Date.now();
          },
          () => {
            skillBootstrapSettled = true;
            skillResolveSettledAt = Date.now();
          }
        );

        // ── THINKING-PHASE TICKET (synchronous) ────────────────────────────────
        // Register an in-flight ticket BEFORE the first await so this tab's
        // persisted `isLoading=true` is never demoted to "Interrupted" by
        // `reconcileLoadingWithRegistry` on a file switch + switch-back during
        // the thinking phase. `capturePresentationKey` is guaranteed to
        // return the SAME `documentId` we read here synchronously (it anchors
        // identity to the sync `getDocumentId()` and only enriches the ref
        // afterward), so this ticket tag stays correct for the lifetime of
        // the flow — no late re-tagging required.
        thinkingTicket = registerThinkingPhase(sourceDocumentId, sourceTabId);
        // The Skill snapshot is already frozen. Resolve this call at handoff so
        // the composer can clear chips without waiting for the full pipeline.
        async function continueAcceptedQuery() {
          try {
            await langfuseRunStart;

            // ── PRESENTATION IDENTITY CAPTURE (async) ──────────────────────────────
            // Snapshot the presentation we're targeting so every subsequent
            // mutation in this flow routes to the SAME file even if the user
            // switches active window mid-flight. The async portion only
            // populates `presentationRef.name` / `fullName` for display; the
            // `documentId` matches `sourceDocumentId` above. See
            // f2_docs/powerpoint/identifying_presentations_from_finster_chat.md.
            const presentationKey = await capturePresentationKey(log);
            const authoritativeDocumentId = presentationKey.documentId || sourceDocumentId;
            if (authoritativeDocumentId !== sourceDocumentId) {
              retargetThinkingPhaseTicket(thinkingTicket, authoritativeDocumentId);
            }

            // Route a TabsState updater either through React's setTabsState (when
            // the user is still on the source presentation) or directly to that
            // presentation's storage envelope (when they've switched away). Both
            // paths converge on `updateTab(prev, sourceTabId, ...)` which is a
            // safe no-op if `sourceTabId` is missing from `prev`.
            //
            // Branch 1 ALSO writes the result to disk synchronously under
            // `sourceDocumentId`. The `useTabs` persist effect would normally
            // handle this, but it fires async — and `setActivePresentationId(...)`
            // can flip the active id between when the updater is committed and
            // when the effect runs (e.g. when the FactSet poll observes a file
            // switch in the same animation frame). Without an eager write, those
            // in-flight user-message / thinking updates would be persisted under
            // the NEW file's envelope (or lost entirely if React replaced state
            // wholesale for the incoming file), which is the "comes back with just
            // the applied changes" symptom we saw on switch.
            // Inside-commit guard: `prev` can belong to a different file than
            // `sourceDocumentId` if the FactSet poll flipped presentations
            // between scheduling and committing. Demote to storage-only so the
            // source file's envelope still gets the update and React state for
            // the unrelated file isn't polluted.
            const applyToSourceTabs = (updater: (prev: TabsState) => TabsState): void => {
              if (accountScopeKeyRef.current !== sourceAccountScopeKey) return;
              const buildSourceFallbackState = (): TabsState => ({
                documentId: authoritativeDocumentId,
                tabs: [
                  {
                    ...activeTab,
                    id: sourceTabId,
                    session: {
                      ...activeTab.session,
                    },
                  },
                ],
                activeTabId: sourceTabId,
              });
              const applyViaStorage = () => {
                const sourceState =
                  loadTabsStateForDocumentId(authoritativeDocumentId, sourceAccountScopeKey) ??
                  buildSourceFallbackState();
                const next = updater(sourceState);
                if (next !== sourceState) {
                  saveTabsStateForDocumentId(authoritativeDocumentId, next, sourceAccountScopeKey);
                }
              };

              if (getDocumentId() === authoritativeDocumentId) {
                setTabsState((prev) => {
                  if (accountScopeKeyRef.current !== sourceAccountScopeKey) {
                    return prev;
                  }
                  if (prev.documentId && prev.documentId !== authoritativeDocumentId) {
                    applyViaStorage();
                    return prev;
                  }
                  const next = updater(prev);
                  if (next !== prev) {
                    try {
                      saveTabsStateForDocumentId(
                        authoritativeDocumentId,
                        next,
                        sourceAccountScopeKey
                      );
                    } catch {
                      // Storage layer already logs; don't break React's commit.
                    }
                  }
                  return next;
                });
                return;
              }
              applyViaStorage();
            };

            const useFactSetBridge = shouldUseFactSetBridge();
            const allowActiveGraphForRequest =
              allowActiveGraph && useFactSetBridge && isPowerPointNativeAgentActive();
            // Every gate must hold before the model learns the route exists.
            const allowPreslinkChartCreationForRequest =
              chartRoutingGate.preslinkEnabled &&
              useFactSetBridge &&
              isPowerPointNativeAgentActive() &&
              isPreslinkCreateDataConfigured();
            const preslinkTurnPresentationContext = allowPreslinkChartCreationForRequest
              ? getPreslinkCreationPresentationContextViaIntents(log)
              : undefined;
            const preslinkTurnSelectedShapes = allowPreslinkChartCreationForRequest
              ? getSelectedShapesViaIntents({ log, timeoutMs: 12_000 })
              : undefined;
            // The chart tool awaits these; the side catches only stop unhandled
            // rejections on turns that never ask for a chart.
            preslinkTurnPresentationContext?.catch(() => undefined);
            preslinkTurnSelectedShapes?.catch(() => undefined);
            const toolPolicy: ToolExecutionPolicy = {
              ...(brandId ? { brandId } : undefined),
              allowPreslink: useFactSetBridge,
              ...(allowActiveGraphForRequest ? { allowActiveGraph: true } : {}),
              ...(allowPreslinkChartCreationForRequest
                ? { allowPreslinkChartCreation: true }
                : {}),
              // This is a host-capability gate. The main agent must still explicitly
              // choose operation:"refresh" before any destructive work.
              allowPreslinkProcessing: useFactSetBridge,
              enableSnippetGuidance: resolveSnippetPolicy({ brandId }).enabled,
              finsterDataSources: researchScope.dataSources,
              userFileIds: researchScope.userFileIds,
              publicBrandName: useFactSetBridge ? "FactSet AI" : "Finster",
            };
            const brand = getBrandContext(toolPolicy);

            let fullQuery = withPlannerModeInstruction(query, plannerMode);
            const hasContext = !!taskContext;
            if (taskContext) {
              fullQuery = buildTaskContextPrompt({
                taskName: taskContext.name,
                taskContent: taskContext.content,
                userQuery: fullQuery,
                toolPolicy,
              });
              log(`Including task context: ${taskContext.name}`);
            }
            if (reviewPlanAtSubmit) {
              fullQuery = `${buildPendingDeckPlanPrompt(reviewPlanAtSubmit)}\n\n${fullQuery}`;
              log(`Including unconfirmed deck plan (${reviewPlanAtSubmit.slides.length} slides)`);
            }

            const setLoadingStatus = (status: string) => {
              applyToSourceTabs((prev) => updateTab(prev, sourceTabId, { loadingStatus: status }));
            };
            const syncSourceTabsWithPresentation = async (reason: string): Promise<void> => {
              const activeDocumentId = getDocumentId();
              if (!shouldSyncTabsForSourceDocument(activeDocumentId, authoritativeDocumentId)) {
                log(
                  `[multi-file] Skipping ${reason} tab sync — active doc (${activeDocumentId}) is not the source (${authoritativeDocumentId}).`
                );
                return;
              }
              await syncTabsWithPresentation(reason);
            };
            const setIsLoading = (loading: boolean) => {
              applyToSourceTabs((prev) => updateTab(prev, sourceTabId, { isLoading: loading }));
            };
            const ownsLoadingState = () =>
              queryOwnsLoadingState(activeQueryAbortRef.current, sourceTabId, abortController);
            // Local mirror of the persisted thought-process text. `activeTab`
            // is captured by closure at the start of handleQuery and never
            // re-reads from the React state tree, so `activeTab.streamingThinking`
            // would always be the initial empty string when read later in
            // `onComplete`.
            //
            // Keep model reasoning and tool-result summaries in separate buffers:
            // providers may emit another `onThinking` callback after a
            // `finster_query` result, and replacing the whole string there would
            // drop the already-rendered Finster data from the persisted
            // Thought-process accordion.
            let latestStreamingThinking = "";
            let latestReasoningThinking = "";
            let latestToolResultThinking = "";
            const composeStreamingThinking = () =>
              `${latestReasoningThinking}${latestToolResultThinking}`;
            const setStreamingThinking = (thinking: string) => {
              latestStreamingThinking = thinking;
              applyToSourceTabs((prev) =>
                updateTab(prev, sourceTabId, { streamingThinking: thinking })
              );
            };
            const collapseThoughtProcess = () => {
              applyToSourceTabs((prev) =>
                updateTab(prev, sourceTabId, { thoughtProcessCollapseSignal: Date.now() })
              );
            };
            const setRefinementStep = (step: number) => {
              applyToSourceTabs((prev) => updateTab(prev, sourceTabId, { refinementStep: step }));
            };
            const setVisionFeedback = (feedback: string) => {
              applyToSourceTabs((prev) =>
                updateTab(prev, sourceTabId, { visionFeedback: feedback })
              );
            };
            // Live agent hop timeline. Events arrive per-token from the stream, so we
            // fold into a local accumulator and flush a snapshot to tab state on a rAF
            // tick to avoid a re-render per token. Additive to streamingThinking.
            let latestAgentTimeline = emptyTimeline();
            let agentTimelineFlushPending = false;
            const flushAgentTimeline = () => {
              agentTimelineFlushPending = false;
              if (abortSignal.aborted || !ownsLoadingState()) return;
              applyToSourceTabs((prev) =>
                updateTab(prev, sourceTabId, { agentTimeline: latestAgentTimeline })
              );
            };
            const scheduleAgentTimelineFlush = () => {
              if (agentTimelineFlushPending) return;
              agentTimelineFlushPending = true;
              if (typeof requestAnimationFrame !== "undefined")
                requestAnimationFrame(flushAgentTimeline);
              else setTimeout(flushAgentTimeline, 32);
            };
            const resetAgentTimeline = () => {
              latestAgentTimeline = emptyTimeline();
              applyToSourceTabs((prev) =>
                updateTab(prev, sourceTabId, { agentTimeline: undefined })
              );
            };
            const clearLoadingState = () => {
              if (!ownsLoadingState()) return;
              applyToSourceTabs((prev) => clearQueryLoadingStateForTab(prev, sourceTabId));
            };
            const finishIfCancelled = (): boolean => {
              if (
                !abortSignal.aborted &&
                accountScopeKeyRef.current === sourceAccountScopeKey &&
                isCurrentReviewGeneration()
              ) {
                return false;
              }
              if (!abortSignal.aborted) {
                abortController.abort();
              }
              trackQueryFailure("cancelled");
              clearLoadingState();
              if (ownsLoadingState()) {
                setResult(null);
              }
              return true;
            };

            const preslinkProcessingPrompt = buildPreslinkProcessingPrompt(
              fullQuery,
              chatHistoryForModel
            );

            // Turn-scoped citation collection. Envelopes come from attached
            // task output and/or each `finster_query` tool call; we de-dupe
            // by uuid (first occurrence wins) so the same citation
            // referenced from multiple sources only lands on the
            // resulting `ChatMessage` once. See `createTurnCitationCollector`
            // and `mergeCitationsIntoTurn` for the underlying helpers.
            const turnCollector = createTurnCitationCollector();
            if (taskContext?.citations?.length) {
              mergeCitationsIntoTurn(turnCollector, taskContext.citations);
              log(
                `[CITATIONS] task context contributed ${taskContext.citations.length} citation(s); turn now has ${turnCollector.citations.length}`
              );
            }
            // Real-id-keyed citation bindings accumulated across every apply_edits
            // call this turn (from the apply pipeline's manifest). Real ids mean no
            // createdSlides diff / positional pairing is needed downstream.
            const turnCitationBindings: SlideCitationBinding[] = [];

            const goldenArtifactRun = isGoldenArtifactCaptureEnabled()
              ? createGoldenArtifactRun()
              : null;
            const isGoldenCaptureMode = Boolean(goldenArtifactRun);
            if (goldenArtifactRun) {
              log(`[GOLDEN] Capture enabled for run ${goldenArtifactRun.runId}`);
            }
            // Turn-scoped allocator: every native tool call this turn captures into
            // its own subdirectory. apply_edits keeps the apply_NN/ naming convention
            // used for operations.json + prepared_batch_operations.json; observe/data
            // tools use tool_NN_<tool>/.
            const nextGoldenToolSubdirectory = createToolArtifactSubdirectoryAllocator();

            const runChatPipeline = async (): Promise<void> => {
              try {
                if (finishIfCancelled()) return;
                setLoadingStatus("Reading slide content...");
                log("Extracting presentation content...");
                // Wave 3 A2: drop deck sections and per-shape decorative formatting the
                // LLM context envelope never serializes anyway. Keeps every field the
                // model actually reasons about (text, fill, border, chart, table,
                // placeholderFormat, pictureFormat).
                //
                // Wave 4 fix 1: include `pageSetup: true` so the host returns slide
                // width/height inside this scoped extract. Without it, the downstream
                // `resolveSlideBoundsDimensions` falls through to a second unfiltered
                // `RunOfficeExtractor` round-trip (~2-3s on a 26-slide deck, much more
                // on 200-slide decks) purely to get `slideWidth`/`slideHeight`.
                // NOTE: deliberately NOT passing `presentationRef` here. Threading
                // the FactSet ref turned `bypassSharedCache` on in the extractor,
                // which made the LLM see fresh-but-noisier shape data than on main
                // (cold extract vs the cached extract main uses) and visibly
                // degraded edit decisions. Multi-file storage routing is handled
                // separately via `applyToSourceTabs` / coordinator — the model
                // input path stays exactly as on main.
                //
                // Wave 5: restrict the extract to a symmetric window of slides
                // around the currently selected one. The window is sized by
                // `CHAT_CONTEXT_WINDOW_SIZE` and resolved via the two cheap
                // intents `GetActivePresentationContext` + `GetSlideCount`, then
                // forwarded as `filters.slides.ranges` per the FactSet schema in
                // `f2_docs/powerpoint/office-extractor/filters/slides.md`. The
                // wrapper falls back to an unscoped extract if either intent
                // fails or the deck is smaller than the window.
                const chatExtractionFilters = {
                  slides: {
                    notesPage: false,
                    background: false,
                    headersFooters: false,
                    shapes: {
                      glow: false,
                      reflection: false,
                      shadow: false,
                      softEdge: false,
                      callout: false,
                      textEffectFormat: false,
                      connectorFormat: false,
                      tags: false,
                    },
                  },
                  designs: false,
                  notesMaster: false,
                  pageSetup: true,
                };
                let presentationContent = await extractWindowedPresentationContent(log, {
                  forceRefresh: true,
                  windowSize: CHAT_CONTEXT_WINDOW_SIZE,
                  filters: chatExtractionFilters,
                });
                if (finishIfCancelled()) return;
                log(`Found ${presentationContent.slides.length} slides`);
                let selectedSlide =
                  presentationContent.slides.find(
                    (slide) => slide.index === presentationContent.selectedSlideIndex
                  ) || presentationContent.slides[presentationContent.selectedSlideIndex];
                const selectedSlideNumber =
                  selectedSlide?.slideNumber ?? presentationContent.selectedSlideIndex + 1;
                log(
                  `📍 Selected slide index: ${presentationContent.selectedSlideIndex} (Slide ${selectedSlideNumber})`
                );
                applyToSourceTabs((prev) => {
                  const currentTab = prev.tabs.find((t) => t.id === sourceTabId);
                  if (!currentTab) return prev;
                  return updateTab(prev, sourceTabId, {
                    session: {
                      ...currentTab.session,
                      slideIndex: presentationContent.selectedSlideIndex,
                      slideNumber: selectedSlideNumber,
                      name: `Slide ${selectedSlideNumber}`,
                      ...(selectedSlide?.id ? { slideId: selectedSlide.id } : {}),
                      messages: currentTab.session.messages.map((message) =>
                        message.id === userMessage.id
                          ? {
                              ...message,
                              slideIndex: presentationContent.selectedSlideIndex,
                              slideNumber: selectedSlideNumber,
                            }
                          : message
                      ),
                      updatedAt: Date.now(),
                    },
                  });
                });

                // Wave 4 fix 4: `getSelectedTableData()` only returns data when the
                // user has a table actively selected in PowerPoint, so attempting it
                // for tables on non-selected slides is structurally useless — it
                // either returns nothing (12s timeout) or returns the wrong table's
                // cell data. Limit the fallback to one attempt on the currently
                // selected slide; if that yields data, propagate it. On a 26-slide
                // deck with several empty-cell tables this drops ~5s of synchronous
                // `GetSelectedShapes` round-trips per chat extract.
                const selectedSlideIndex = presentationContent.selectedSlideIndex;
                let selectedSlideTableFallbackAttempted = false;
                for (const slide of presentationContent.slides) {
                  log(`  Slide ${slide.index}: ${slide.shapes.length} shapes`);
                  for (const shape of slide.shapes) {
                    if (shape.type === "table") {
                      if (
                        shape.tableData &&
                        shape.tableData.rows.some((row) => row.some((cell) => cell.trim()))
                      ) {
                        log(`    - table: ${shape.tableData.rowCount}x${shape.tableData.colCount}`);
                        log(`      cells: ${JSON.stringify(shape.tableData.rows)}`);
                      } else if (
                        slide.index === selectedSlideIndex &&
                        !selectedSlideTableFallbackAttempted
                      ) {
                        log(`    - table: (no cell data via PowerPoint API)`);
                        log(`      💡 TIP: Select the table in PowerPoint, then re-run query`);

                        selectedSlideTableFallbackAttempted = true;
                        const tableData = await getSelectedTableData();
                        if (
                          tableData &&
                          tableData.rows.some((row) => row.some((cell) => cell.trim()))
                        ) {
                          log(
                            `      ✓ Got data via selection: ${tableData.rowCount}x${tableData.colCount}`
                          );
                          log(`      cells: ${JSON.stringify(tableData.rows)}`);
                          shape.tableData = tableData;
                        }
                      } else {
                        log(`    - table: (no cell data; skipping selection fallback off-slide)`);
                      }
                    } else {
                      log(`    - ${shape.type}: "${shape.text?.slice(0, 30) || "(no text)"}..."`);
                    }
                  }
                }

                if (presentationContent.slides.length === 0) {
                  trackQueryFailure("empty_presentation");
                  setResult({
                    success: false,
                    message: "No slides found in the presentation",
                  });
                  setIsLoading(false);
                  return;
                }

                // Turn-scoped context shared by every main-agent tool hop. PresLink may
                // replace slides mid-loop, so this context also carries the latest fresh
                // presentation and reports replacement ids back into the owning chat flow.
                const agentToolContext: AgentToolContext = {
                  preslinkProcessingPrompt,
                  userPrompt: fullQuery,
                  presentationRef: presentationKey.presentationRef ?? null,
                  latestPresentationContent: presentationContent,
                  ...(allowActiveGraphForRequest
                    ? { activeGraphCreateResults: new Map() }
                    : {}),
                  ...(preslinkTurnPresentationContext
                    ? {
                        preslinkChartCreateResults: new Map(),
                        preslinkTurnPresentationContext,
                        preslinkTurnSelectedShapes,
                      }
                    : {}),
                  preslinkExtractionFilters: chatExtractionFilters,
                  slideBounds:
                    presentationContent.slideWidth > 0 && presentationContent.slideHeight > 0
                      ? {
                          slideWidth: presentationContent.slideWidth,
                          slideHeight: presentationContent.slideHeight,
                        }
                      : null,
                  signal: abortSignal,
                  goldenArtifactRun,
                  nextGoldenToolSubdirectory,
                  onProgress: (message) => {
                    if (abortSignal.aborted) return;
                    setLoadingStatus(message);
                    log(message);
                  },
                  log,
                  companyId,
                  pendingDeckPlan: reviewPlanAtSubmit,
                  blockNewSlideCreation: Boolean(reviewPlanAtSubmit),
                  ...(reviewOperationId
                    ? {
                        onResearchResult: (researchResult) => {
                          if (
                            abortSignal.aborted ||
                            !isCurrentReviewGeneration() ||
                            !ownsSubmissionAccount()
                          ) {
                            return;
                          }
                          planningEvidence = appendPlanningEvidence({
                            reviewOperationId,
                            current: planningEvidence,
                            tool: researchResult.tool,
                            query: researchResult.query,
                            answer: researchResult.answer,
                            sessionId: researchResult.sessionId,
                            citations: researchResult.citations,
                          });
                          if (reviewPlanAtSubmit || agentToolContext.blockNewSlideCreation) {
                            onReviewPlanningEvidenceRef.current?.(
                              reviewOperationId,
                              reviewOperationGeneration,
                              planningEvidence
                            );
                          }
                        },
                      }
                    : undefined),
                  getPendingDeckPlan: getPendingDeckPlanRef.current
                    ? () => getPendingDeckPlanRef.current?.()
                    : undefined,
                  isReviewSessionOpen: isReviewSessionOpenRef.current
                    ? () => Boolean(isReviewSessionOpenRef.current?.())
                    : undefined,
                  onPresentDeckPlan: async (plan) => {
                    if (
                      abortSignal.aborted ||
                      !isCurrentReviewGeneration() ||
                      !registerReviewOperationActivity()
                    ) {
                      return;
                    }
                    const boundPlan = bindTransientReviewContext(plan);
                    agentToolContext.pendingDeckPlan = boundPlan;
                    agentToolContext.blockNewSlideCreation = true;
                    await onPresentDeckPlanRef.current?.(boundPlan, {
                      brandId: brandId ?? null,
                    });
                    if (abortSignal.aborted || !isCurrentReviewGeneration()) {
                      return;
                    }
                    reviewEvidenceBound = true;
                    reviewSkillBindingRef.current = reviewSkillBinding;
                  },
                  onUpdateDeckPlan: (plan) => {
                    if (abortSignal.aborted || !isCurrentReviewGeneration()) {
                      return false;
                    }
                    const boundPlan = bindTransientReviewContext(plan);
                    const applied = onUpdateDeckPlanRef.current?.(boundPlan);
                    if (applied !== false && !abortSignal.aborted && isCurrentReviewGeneration()) {
                      reviewSkillBindingRef.current = reviewSkillBinding;
                    }
                    return applied;
                  },
                };
                agentToolContext.reportPreslinkRefresh = ({
                  presentationContent: freshPresentationContent,
                  excludedShapeIdsBySlideId,
                }) => {
                  presentationContent = freshPresentationContent;
                  agentToolContext.latestPresentationContent = freshPresentationContent;
                  agentToolContext.slideBounds =
                    freshPresentationContent.slideWidth > 0 &&
                    freshPresentationContent.slideHeight > 0
                      ? {
                          slideWidth: freshPresentationContent.slideWidth,
                          slideHeight: freshPresentationContent.slideHeight,
                        }
                      : agentToolContext.slideBounds;
                  const refreshedSelectedSlide =
                    freshPresentationContent.slides.find(
                      (slide) => slide.index === freshPresentationContent.selectedSlideIndex
                    ) ||
                    freshPresentationContent.slides[freshPresentationContent.selectedSlideIndex];
                  const refreshedSelectedSlideNumber =
                    refreshedSelectedSlide?.slideNumber ??
                    freshPresentationContent.selectedSlideIndex + 1;
                  selectedSlide = refreshedSelectedSlide;
                  if (refreshedSelectedSlide?.id) {
                    applyToSourceTabs((prev) =>
                      rebindTabToRefreshedSlide(prev, {
                        tabId: sourceTabId,
                        slide: {
                          id: refreshedSelectedSlide.id,
                          index: freshPresentationContent.selectedSlideIndex,
                          slideNumber: refreshedSelectedSlideNumber,
                        },
                        userMessageId: userMessage.id,
                      })
                    );
                  }
                  langfuseRunContext.deck =
                    summarizePresentationForLangfuse(freshPresentationContent);
                  log(
                    `PresLink refresh complete; re-extracted ${freshPresentationContent.slides.length} slide(s) with fresh ids (slide ${refreshedSelectedSlideNumber}). ` +
                      `Excluding linked shapes on ${excludedShapeIdsBySlideId.size} refreshed slide(s) from native edits.`
                  );
                };

                // Ask the host which shapes on the selected slide are PresLink-linked so
                // the model can see that their values come from a refresh rather than
                // from research. Started before the image capture so the intent
                // round-trip overlaps it.
                const preslinkMarkerProbe = probeSelectedSlidePreslinkRefs({
                  content: presentationContent,
                  selectedSlide,
                  enabled: shouldUseFactSetBridge(),
                  ctx: agentToolContext,
                });
                // Charts this add-in linked carry the parameters they were built
                // from, so a chart-setting change such as "use the Quartz theme"
                // can rebuild the same chart; data-only changes refresh it in place.
                const preslinkChartSlideId = selectedSlide?.id;
                const createdPreslinkChartProbe =
                  toolPolicy.allowPreslinkChartCreation === true && preslinkChartSlideId
                    ? readCreatedPreslinkCharts(preslinkChartSlideId, agentToolContext).catch(
                        (probeError) => {
                          log(
                            `⚠️ Could not read linked FactSet charts: ${redactLocalPathText(String(probeError))}`
                          );
                          return new Map<string, Record<string, unknown>>();
                        }
                      )
                    : Promise.resolve(new Map<string, Record<string, unknown>>());

                setLoadingStatus("Capturing slide image...");
                let slideImageBase64: string | undefined;
                try {
                  // Same reasoning as the windowed extract above: skip
                  // `presentationRef` so the image we send to the LLM is captured
                  // exactly the way main captures it (active-context-resolved,
                  // cache-friendly). Cross-file routing of the *flow* is enforced
                  // upstream by the coordinator and `applyToSourceTabs`.
                  slideImageBase64 = await exportSlideAsImage(
                    presentationContent.selectedSlideIndex
                  );
                  if (finishIfCancelled()) return;
                  log(`📷 Captured slide image (${(slideImageBase64.length / 1024).toFixed(0)}KB)`);
                } catch (imgErr) {
                  log(`⚠️ Could not capture slide image: ${imgErr}`);
                }

                const preslinkLinkedRefs = await preslinkMarkerProbe;
                if (finishIfCancelled()) return;
                if (preslinkLinkedRefs.length > 0) {
                  presentationContent = applyPreslinkLinkedMarkers(
                    presentationContent,
                    preslinkLinkedRefs
                  );
                  agentToolContext.latestPresentationContent = presentationContent;
                  log(
                    `🔗 Marked ${preslinkLinkedRefs.length} PresLink-linked shape(s) on the selected slide`
                  );
                }
                const createdPreslinkCharts = await createdPreslinkChartProbe;
                if (finishIfCancelled()) return;
                if (createdPreslinkCharts.size > 0 && preslinkChartSlideId) {
                  presentationContent = applyCreatedPreslinkChartMarkers(
                    presentationContent,
                    preslinkChartSlideId,
                    createdPreslinkCharts
                  );
                  agentToolContext.latestPresentationContent = presentationContent;
                  log(
                    `📈 Marked ${createdPreslinkCharts.size} linked FactSet chart(s) on the selected slide`
                  );
                }

                await runGoldenCaptureStep(goldenArtifactRun, log, "before.pptx", async () => {
                  const beforeDeckBase64 = await exportPresentationAsBase64Compressed();
                  await uploadGoldenArtifactPptx(
                    goldenArtifactRun!.runId,
                    "before",
                    beforeDeckBase64
                  );
                });
                await runGoldenCaptureStep(goldenArtifactRun, log, "llm_input.json", async () => {
                  const preparedRequest = prepareRequestData(
                    presentationContent,
                    fullQuery,
                    chatHistoryForModel
                  );
                  await uploadGoldenArtifactJson(goldenArtifactRun!.runId, "llm_input", {
                    runId: goldenArtifactRun!.runId,
                    generatedAt: new Date().toISOString(),
                    userQuery: query,
                    fullQuery,
                    modelQuery: fullQuery,
                    hasTaskContext: hasContext,
                    selectedSlideIndex: presentationContent.selectedSlideIndex,
                    selectedSlideNumber: preparedRequest.contextEnvelope.selectedSlideNumber,
                    slideImageIncluded: Boolean(slideImageBase64),
                    slideImageBase64: slideImageBase64 || null,
                    historyMessageCount: chatHistoryForModel.length,
                    contextEnvelope: preparedRequest.contextEnvelope,
                    contextStats: preparedRequest.contextStats,
                    textMessage: preparedRequest.textMessage,
                    brandId: brandId ?? null,
                    toolPolicy: {
                      brandId: toolPolicy.brandId ?? null,
                      enableSnippetGuidance: toolPolicy.enableSnippetGuidance,
                      allowActiveGraph: toolPolicy.allowActiveGraph === true,
                      allowPreslink: toolPolicy.allowPreslink === true,
                      allowPreslinkChartCreation:
                        toolPolicy.allowPreslinkChartCreation === true,
                      publicBrandName: toolPolicy.publicBrandName ?? null,
                    },
                  });
                });

                if (!skillBootstrapPromise) {
                  throw new Error("Skills bootstrap was not initialized for this submission.");
                }
                if (!skillBootstrapSettled) {
                  setLoadingStatus(skillLoadingStatus("resolving"));
                }
                const skillBootstrap = await skillBootstrapPromise;
                if (!ownsSubmissionAccount()) {
                  abortController.abort();
                  finishIfCancelled();
                  return;
                }
                if (finishIfCancelled() || skillBootstrap.status === "aborted") return;
                skillRunContext = skillBootstrap.context;
                onSkillDecisionRef.current?.(
                  submissionId,
                  skillBootstrap.skillDecision ?? null,
                  query
                );
                const resolvedReviewBinding:
                  | (ReviewSkillBinding & {
                      state: Exclude<ReviewSkillBindingState, "unbound">;
                    })
                  | null =
                  skillBootstrap.status === "resolved" && skillRunContext
                    ? {
                        reviewOperationId,
                        state: "bound",
                        context: skillRunContext,
                      }
                    : skillBootstrap.status === "cleared"
                      ? {
                          reviewOperationId,
                          state: "cleared",
                          context: undefined,
                        }
                      : skillBootstrap.status === "inherited" &&
                          reviewSkillBinding.state !== "unbound"
                        ? {
                            ...reviewSkillBinding,
                            reviewOperationId,
                          }
                        : null;
                const shouldCommitReviewContext = shouldCommitReviewSkillContext(
                  skillBootstrap.status
                );
                if (reviewPlanAtSubmit && resolvedReviewBinding && shouldCommitReviewContext) {
                  const commitReviewContext = onReviewSkillContextResolvedRef.current;
                  const committed = commitReviewContext
                    ? commitReviewContext(
                        reviewOperationId,
                        reviewOperationGeneration,
                        resolvedReviewBinding.state,
                        resolvedReviewBinding.context
                      ) !== false
                    : true;
                  if (!committed) {
                    abortController.abort();
                    finishIfCancelled();
                    return;
                  }
                  if (isCurrentReviewGeneration()) {
                    reviewSkillBinding = resolvedReviewBinding;
                    reviewSkillBindingRef.current = resolvedReviewBinding;
                    reviewEvidenceBound = true;
                  }
                } else if (resolvedReviewBinding) {
                  reviewSkillBinding = resolvedReviewBinding;
                }
                const skillResolutionStatus = skillBootstrap.status;
                const resolvedSkillContext = skillBootstrap.context;
                const skillResolutionDiagnostics = skillBootstrap.resolution;
                langfuseRunContext.skills = toLangfuseRunSkills(resolvedSkillContext?.skills ?? []);
                skillAnalytics = buildQuerySkillsAnalytics({
                  selectionPath:
                    resolvedSkillContext?.selectionSource ??
                    (skillBootstrap.mode === "explicit" ? "explicit" : "none"),
                  status: skillResolutionStatus,
                  origins: resolvedSkillContext?.skills.map((skill) => skill.origin),
                  skillCount:
                    resolvedSkillContext?.skills.length ??
                    skillResolutionDiagnostics?.skillCount ??
                    0,
                  policyVersion:
                    resolvedSkillContext?.skillPolicyVersion ??
                    skillResolutionDiagnostics?.skillPolicyVersion,
                  resolveLatencyMs:
                    skillResolveStartedAt === undefined
                      ? undefined
                      : (skillResolveSettledAt ?? Date.now()) - skillResolveStartedAt,
                  fallback: skillBootstrap.status === "fail-open" ? true : undefined,
                  effectiveSourceCount:
                    resolvedSkillContext?.effectiveDataSources?.length ??
                    skillResolutionDiagnostics?.effectiveSourceCount ??
                    undefined,
                  effectiveFileCount:
                    resolvedSkillContext?.effectiveExplorerContext.userFileIds?.length ??
                    skillResolutionDiagnostics?.effectiveFileCount ??
                    undefined,
                  guidanceChars:
                    resolvedSkillContext?.guidanceChars ??
                    skillResolutionDiagnostics?.guidanceChars,
                });
                if (skillAnalytics) {
                  postPptxLangfuseObservation(langfuseRunContext, {
                    name: "pptx.skills.resolve",
                    metadata: { ...skillAnalytics },
                    ...(skillBootstrap.status === "blocked"
                      ? { level: "ERROR" as const }
                      : undefined),
                    ...(skillResolveStartedAt === undefined
                      ? undefined
                      : { startTime: new Date(skillResolveStartedAt).toISOString() }),
                    endTime: new Date(skillResolveSettledAt ?? Date.now()).toISOString(),
                  });
                }
                // One refusal path for every Skill policy stop, so a refused run
                // always leaves the same visible trace in the thread.
                const failRunWithSkillMessage = (message: string, errorType: string) => {
                  const errorAssistantMessage: ChatMessage = {
                    id: generateId(),
                    role: "assistant",
                    content: sanitizeUserFacingText(message),
                    timestamp: Date.now(),
                    slideIndex: presentationContent.selectedSlideIndex,
                    slideNumber:
                      selectedSlide?.slideNumber ?? presentationContent.selectedSlideIndex + 1,
                  };
                  applyToSourceTabs((prev) => {
                    const currentTab = prev.tabs.find((tab) => tab.id === sourceTabId);
                    if (!currentTab) return prev;
                    return updateTab(prev, sourceTabId, {
                      session: {
                        ...currentTab.session,
                        messages: [...currentTab.session.messages, errorAssistantMessage],
                        updatedAt: Date.now(),
                      },
                    });
                  });
                  setResult({ success: false, message });
                  trackQueryFailure(errorType);
                };
                if (skillBootstrap.status === "blocked") {
                  failRunWithSkillMessage(
                    toUserFriendlyRequestError(
                      skillBootstrap.failure?.message || "Selected Skills could not be resolved."
                    ),
                    "skills_resolution_error"
                  );
                  return;
                }
                if (skillBootstrap.status === "fail-open") {
                  log(
                    `[SKILLS] Automatic resolution failed open (${skillBootstrap.failure?.kind || "unknown"}); continuing without a Skill.`
                  );
                }
                let skillTargetFilesAttached = true;
                if (skillRunContext) {
                  const skillScope = applySkillRunContextToRuntime(
                    skillRunContext,
                    toolPolicy,
                    agentToolContext
                  );
                  if (skillScope.status === "blocked") {
                    // Fail closed: the user chose this Skill, and a run that
                    // quietly leaves some of its target files behind reads as a
                    // complete one.
                    failRunWithSkillMessage(skillScope.message, "skills_file_limit");
                    return;
                  }
                  if (skillScope.omittedTargetCount > 0) {
                    skillTargetFilesAttached = false;
                    log(
                      `[SKILLS] Skill target files not attached: ${skillScope.omittedTargetCount} file(s) would exceed the per-request limit. Continuing without them.`
                    );
                  }
                  skillMetadata = createSkillTurnMetadata({
                    skills: skillRunContext.skills,
                    selectionPath: skillRunContext.selectionSource,
                    runContextId: skillRunContext.runContextId,
                    policyVersion: skillRunContext.skillPolicyVersion,
                    effectiveSourceCount: skillRunContext.effectiveDataSources?.length,
                    effectiveFileCount:
                      skillRunContext.effectiveExplorerContext.userFileIds?.length,
                    guidanceChars: skillRunContext.guidanceChars,
                  });
                  userMessage = { ...userMessage, skillMetadata };
                  applyToSourceTabs((prev) => {
                    const currentTab = prev.tabs.find((tab) => tab.id === sourceTabId);
                    if (!currentTab) return prev;
                    return updateTab(prev, sourceTabId, {
                      session: {
                        ...currentTab.session,
                        messages: currentTab.session.messages.map((message) =>
                          message.id === userMessage.id ? userMessage : message
                        ),
                        updatedAt: Date.now(),
                      },
                    });
                  });
                  log(
                    `[SKILLS] ${skillBootstrap.status} ${skillRunContext.skills
                      .map((skill) => `/${skill.slug}`)
                      .join(
                        ", "
                      )} (${skillRunContext.selectionSource}, ${skillRunContext.guidanceChars} chars).`
                  );
                }
                const preludeUserMessages = skillRunContext
                  ? buildSkillPreludeMessages(skillRunContext.guidance, {
                      targetFilesAttached: skillTargetFilesAttached,
                    })
                  : undefined;

                resetAgentTimeline();
                if (skillMetadata) {
                  latestAgentTimeline = reduceAgentEvent(latestAgentTimeline, {
                    kind: "skills_resolved",
                    skills: skillMetadata.skills,
                  });
                  scheduleAgentTimelineFlush();
                }
                setLoadingStatus(skillLoadingStatus("settled"));
                const historyCount = chatHistoryForModel.length + 1;
                log(
                  "Sending to the AI model" +
                    (slideImageBase64 ? " (with image)" : "") +
                    (historyCount > 1 ? ` (${historyCount - 1} history)` : "") +
                    "..."
                );

                // The shared tool context updates this summary again if PresLink replaces
                // slides during the model's tool loop.
                langfuseRunContext.deck = summarizePresentationForLangfuse(presentationContent);
                await new Promise<void>((resolve) => {
                  const narratorSink = createNarratingAgentEventSink({
                    config: getReasoningNarrationConfig(),
                    userQuery: fullQuery,
                    signal: abortSignal,
                    log,
                    onThinking: (thinking: string) => {
                      if (abortSignal.aborted) return;
                      log(`Reasoning: ${thinking.slice(0, 100)}...`);
                      latestReasoningThinking = thinking;
                      setStreamingThinking(composeStreamingThinking());
                    },
                    onAgentEvent: (event) => {
                      if (abortSignal.aborted) return;
                      latestAgentTimeline = reduceAgentEvent(latestAgentTimeline, event);
                      scheduleAgentTimelineFlush();
                    },
                  });

                  queryOpenAIStreaming(
                    presentationContent,
                    fullQuery,
                    {
                      onThinking: narratorSink.onThinking,
                      onAgentEvent: narratorSink.onAgentEvent,
                      onToolCall: (tool: string, args: string) => {
                        if (abortSignal.aborted) return;
                        log(`Calling tool: ${tool}(${args})`);
                        // `args` is the clean payload from `formatToolCallArgs`
                        // (the raw query, never JSON) — render verbatim so the
                        // status line shows the full query the model asked for.
                        setLoadingStatus(
                          isResearchToolName(tool)
                            ? `${brand.researchStatusLabel}: ${args}`
                            : tool === "get_slide_images"
                              ? `Using image tool (get_slide_images): ${args}`
                              : `Fetching ${args}`
                        );
                      },
                      onToolResult: (results) => {
                        if (abortSignal.aborted) return;
                        // Tool-result summaries also feed the "Thought process"
                        // pane (parity with main) — finster_query
                        // answers and get_slide_images capture-counts are
                        // appended below the reasoning so users can see what
                        // ground-truth data the model actually pulled in,
                        // not just its summarized reasoning over it. Removing
                        // these previously turned the pane into a reasoning-
                        // only view that hid the source of any data the
                        // model used.
                        let toolSummary = "";
                        for (const toolResult of results) {
                          const data = toolResult.data as
                            | {
                                answer?: string;
                                logoUrl?: string;
                                results?: Array<{
                                  companyName?: string;
                                  success?: boolean;
                                  logoUrl?: string;
                                  error?: string;
                                  message?: string;
                                  debugLog?: string[];
                                }>;
                                citations?: Citation[];
                                debugLog?: string[];
                                requestedSlideNumbers?: number[];
                                capturedSlideNumbers?: number[];
                                skippedSlideNumbers?: number[];
                                capturedCount?: number;
                                createdSlides?: SlideSessionRef[];
                                appliedCitations?: AppliedCitationsToolPayload;
                              }
                            | undefined;
                          if (toolResult.tool === "get_company_logo") {
                            if (data?.debugLog) {
                              for (const line of data.debugLog) {
                                log(`[LOGO] ${line}`);
                              }
                            }
                            if (Array.isArray(data?.results)) {
                              for (const result of data.results) {
                                if (!result.debugLog) continue;
                                for (const line of result.debugLog) {
                                  log(`[LOGO:${result.companyName || "unknown"}] ${line}`);
                                }
                              }
                            }
                          }
                          if (
                            toolResult.success &&
                            isResearchToolName(toolResult.tool) &&
                            Array.isArray(data?.citations)
                          ) {
                            mergeCitationsIntoTurn(turnCollector, data.citations);
                            // The model emits its declared citation set on
                            // `modifications.citations[]` (see the top-level
                            // `citations` schema field) at apply time, so we
                            // don't extract from `data.answer` here. The
                            // model is responsible for narrowing
                            // finster_query's cited values down to the
                            // subset that ended up on the slide; we just
                            // ingest its declaration in `onComplete`.
                            log(
                              `[CITATIONS] finster_query returned ${data.citations.length} citation(s); turn now has ${turnCollector.citations.length}`
                            );
                          }
                          if (
                            toolResult.success &&
                            toolResult.tool === "apply_edits" &&
                            data?.appliedCitations
                          ) {
                            const beforeCount = turnCitationBindings.length;
                            mergeAppliedCitationsToolPayload(
                              turnCollector,
                              turnCitationBindings,
                              data.appliedCitations
                            );
                            const addedCount = turnCitationBindings.length - beforeCount;
                            if (addedCount > 0) {
                              log(
                                `[CITATIONS] apply_edits bound citations to ${addedCount} slide(s); turn cited value count=${turnCollector.citedValuesByUuid.size}`
                              );
                            }
                          }
                          if (toolResult.success && data) {
                            if (toolResult.tool === "get_slide_images") {
                              const requested = data.requestedSlideNumbers || [];
                              const captured = data.capturedSlideNumbers || [];
                              const skipped = data.skippedSlideNumbers || [];
                              const requestedText = requested.length
                                ? requested.join(", ")
                                : "none";
                              const capturedText = captured.length ? captured.join(", ") : "none";
                              const skippedText = skipped.length ? skipped.join(", ") : "none";
                              const summary =
                                `Tool used: get_slide_images\n` +
                                `Captured slide images: ${capturedText}\n` +
                                `Requested slides: ${requestedText}\n` +
                                `Skipped slides: ${skippedText}`;
                              log(`🖼️ ${summary.replace(/\n/g, " | ")}`);
                              // Double newline between the bold header and the
                              // body produces a real markdown paragraph break so
                              // the rendered pane reads as Header → blank line →
                              // body, not Header→body on adjacent lines.
                              toolSummary += `\n\n**Image Tool Used (\`get_slide_images\`)**\n\n${summary}`;
                            } else if (data.answer) {
                              const preview =
                                data.answer.length > 300
                                  ? data.answer.slice(0, 300) + "..."
                                  : data.answer;
                              log(`📊 ${toolResult.tool} result: ${preview}`);
                              // Mirror the host-mode switch already used by the
                              // onToolCall status line ("Querying FactSet AI" vs
                              // "Querying Finster"): in FactSet host mode the
                              // data label should also read "FactSet AI", not
                              // "Finster Data".
                              const label = isResearchToolName(toolResult.tool)
                                ? brand.researchResultLabel
                                : toolResult.tool;
                              // `\n\n` (not `\n`) after the bold header so the
                              // markdown renderer treats header and body as two
                              // separate paragraphs.
                              toolSummary += `\n\n**${label}:**\n\n${data.answer}`;
                            }
                          } else if (!toolResult.success) {
                            log(`⚠️ ${toolResult.tool} failed: ${toolResult.error}`);
                          }
                        }
                        if (toolSummary) {
                          // Persist tool outputs in their own buffer so a later
                          // provider `onThinking` update cannot overwrite Finster
                          // data that already streamed into the pane.
                          latestToolResultThinking += toolSummary;
                          setStreamingThinking(composeStreamingThinking());
                        }
                        // Mode-neutral: at this point the model hasn't returned yet,
                        // so we don't know whether the response will be an edit (slide
                        // mutation) or a chat answer. "response" works for both.
                        setLoadingStatus("Generating response with real data...");
                      },
                      onComplete: async (modifications: PresentationModification) => {
                        if (abortSignal.aborted) {
                          trackQueryFailure("cancelled");
                          resolve();
                          return;
                        }
                        try {
                          collapseThoughtProcess();
                          setLoadingStatus("Finishing thought process...");
                          const narratorFinalization = await narratorSink.finalize();
                          if (narratorFinalization.timedOut) {
                            log("[NARRATOR] finalization timed out; completing response");
                          }
                          if (abortSignal.aborted) {
                            trackQueryFailure("cancelled");
                            resolve();
                            return;
                          }
                          const modelDiagnostics = modifications.diagnostics || [];

                          if (modelDiagnostics.length > 0) {
                            log(`⚠️ Model diagnostics: ${modelDiagnostics.length}`);
                            for (const diagnostic of modelDiagnostics.slice(0, 5)) {
                              log(`   - ${diagnostic.code}: ${diagnostic.message}`);
                            }
                          }
                          await runGoldenCaptureStep(
                            goldenArtifactRun,
                            log,
                            "operations.json",
                            async () => {
                              const operationsSnapshot = JSON.parse(JSON.stringify(modifications));
                              await uploadGoldenArtifactJson(
                                goldenArtifactRun!.runId,
                                "operations",
                                operationsSnapshot
                              );
                            }
                          );

                          const preslinkCompletionBlockReason =
                            getPreslinkTurnBlockReason(agentToolContext);
                          if (preslinkCompletionBlockReason) {
                            log(`[chat-complete] ${preslinkCompletionBlockReason}`);
                            trackQueryFailure("preslink_refresh_failed");
                            // The streaming pane unmounts when loading clears and the
                            // result panel is not rendered, so the turn's thought
                            // process only survives on a session message.
                            const blockedAssistantMessage: ChatMessage = {
                              id: generateId(),
                              role: "assistant",
                              content: sanitizeUserFacingText(preslinkCompletionBlockReason),
                              timestamp: Date.now(),
                              slideIndex: presentationContent.selectedSlideIndex,
                              slideNumber:
                                selectedSlide?.slideNumber ??
                                presentationContent.selectedSlideIndex + 1,
                              thinking: sanitizeUserFacingText(latestStreamingThinking),
                              agentTimeline: getCappedAgentTimelineForMessage(latestAgentTimeline),
                              ...(skillMetadata ? { skillMetadata } : undefined),
                            };
                            applyToSourceTabs((prev) => {
                              const currentTab = prev.tabs.find((t) => t.id === sourceTabId);
                              if (!currentTab) return prev;
                              return updateTab(prev, sourceTabId, {
                                session: {
                                  ...currentTab.session,
                                  messages: [
                                    ...currentTab.session.messages,
                                    blockedAssistantMessage,
                                  ],
                                  updatedAt: Date.now(),
                                },
                              });
                            });
                            setResult({
                              success: false,
                              message: preslinkCompletionBlockReason,
                              thinking: sanitizeUserFacingText(latestStreamingThinking),
                            });
                            resolve();
                            return;
                          }

                          // === Chat-mode early return ===
                          //
                          // When `mode === "chat"`, the model returned a conversational
                          // answer in `reply` and no slide modifications. We render it
                          // as a plain assistant bubble (`ChatMessage.content`) on the
                          // active tab and skip the entire write/apply/refine pipeline
                          // below. No `registerTabOperation` (no PowerPoint mutation),
                          // no mirror across affected slides (chat answers belong to
                          // the active slide where the user asked), no
                          // `applyModifications`, no vision refinement.
                          if (modifications.mode === "chat") {
                            if (abortSignal.aborted) {
                              trackQueryFailure("cancelled");
                              resolve();
                              return;
                            }
                            mergeDeclaredCitations(turnCollector, modifications.citations);
                            const {
                              citations: compactedCitations,
                              citedValuesByUuid: compactedCitedValuesByUuid,
                            } = compactTurnCitations(turnCollector);
                            // Per-slide citation messages go to the slides the content
                            // actually landed on, keyed by REAL slide id from the apply
                            // manifest. Exclude the source slide here: its own scoped
                            // citations are carried by `chatAssistantMessage` below, so a
                            // synthetic message would double-count it.
                            const isSourceBinding = (binding: SlideCitationBinding): boolean =>
                              selectedSlide?.id
                                ? binding.slideId === selectedSlide.id
                                : binding.slideIndex === presentationContent.selectedSlideIndex;
                            const applyCitationMessages = buildSlideCitationMessages({
                              bindings: turnCitationBindings.filter(
                                (binding) => !isSourceBinding(binding)
                              ),
                              citations: compactedCitations,
                              citedValuesByUuid: compactedCitedValuesByUuid,
                              log,
                            });
                            // Source slide's OWN Citations-tab provenance: strictly
                            // scoped to what the model declared landed on the source
                            // slide. Empty when the edits targeted other slides (e.g.
                            // duplicate-then-fill a new slide).
                            const sourceBindingIds = turnCitationBindings
                              .filter((binding) => isSourceBinding(binding))
                              .flatMap((binding) => binding.declaredCitationIds);
                            const sourceCitationPayload =
                              sourceBindingIds.length > 0
                                ? filterCitationPayloadByIds({
                                    citations: compactedCitations,
                                    citedValuesByUuid: compactedCitedValuesByUuid,
                                    citationIds: sourceBindingIds,
                                  })
                                : {};
                            const replyText = sanitizeUserFacingText(modifications.reply ?? "");
                            const chatAssistantMessage: ChatMessage = {
                              id: generateId(),
                              role: "assistant",
                              content: replyText,
                              timestamp: Date.now(),
                              slideIndex: presentationContent.selectedSlideIndex,
                              slideNumber:
                                selectedSlide?.slideNumber ??
                                presentationContent.selectedSlideIndex + 1,
                              thinking: sanitizeUserFacingText(latestStreamingThinking),
                              agentTimeline: getCappedAgentTimelineForMessage(latestAgentTimeline),
                              ...(skillMetadata ? { skillMetadata } : undefined),
                              // Per-slide provenance for the source slide's Citations tab.
                              citations: sourceCitationPayload.citations,
                              citationsById: sourceCitationPayload.citationsById,
                              citedValuesByUuid: sourceCitationPayload.citedValuesByUuid,
                              // Turn-level retrieved sources for the chat bubble only;
                              // SlideCitationsView ignores these so they never pollute a
                              // slide's Citations tab.
                              retrievedCitations: compactedCitations,
                              retrievedCitedValuesByUuid: compactedCitedValuesByUuid,
                            };
                            applyToSourceTabs((prev) => {
                              const currentTab = prev.tabs.find((t) => t.id === sourceTabId);
                              if (!currentTab) return prev;
                              let next = updateTab(prev, sourceTabId, {
                                session: {
                                  ...currentTab.session,
                                  messages: [...currentTab.session.messages, chatAssistantMessage],
                                  updatedAt: Date.now(),
                                },
                              });
                              if (applyCitationMessages.length > 0) {
                                next = applyCitationMessages.reduce(
                                  (state, entry) =>
                                    attachSyntheticMessageToSlideTab(state, {
                                      slideId: entry.slide.id,
                                      slideIndex: entry.slide.index,
                                      slideNumber: entry.slide.slideNumber,
                                      message: entry.message,
                                    }),
                                  next
                                );
                              }
                              return next;
                            });
                            if (getDocumentId() === authoritativeDocumentId) {
                              setResultsByTabId((prev) => ({
                                ...prev,
                                [sourceTabId]: {
                                  success: true,
                                  message: replyText,
                                  thinking: sanitizeUserFacingText(latestStreamingThinking),
                                },
                              }));
                            }
                            log(
                              `[CHAT] chat-mode response (${replyText.length} chars); skipped applyModifications`
                            );
                            trackQuerySuccess();
                            resolve();
                            return;
                          }

                          // The provider may finish with a terminal edit payload instead
                          // of invoking apply_edits. Enforce the same post-PresLink
                          // fail-closed and exclusion guards before history, mirroring,
                          // or direct host mutation so neither native path can touch
                          // managed objects.
                          const preslinkGuard = guardTerminalPreslinkModifications(
                            modifications,
                            agentToolContext
                          );
                          if (preslinkGuard.status === "blocked") {
                            log(`[chat-apply] ${preslinkGuard.error}`);
                            trackQueryFailure("preslink_refresh_failed");
                            setResult({
                              success: false,
                              message: preslinkGuard.error,
                              thinking: sanitizeUserFacingText(latestStreamingThinking),
                            });
                            resolve();
                            return;
                          }
                          modifications = preslinkGuard.modifications;
                          if (preslinkGuard.skippedShapeIds.length > 0) {
                            log(
                              `[chat-apply] skipped ${preslinkGuard.skippedShapeIds.length} terminal edit(s) targeting PresLink-managed shapes: ${preslinkGuard.skippedShapeIds.join(", ")}`
                            );
                          }
                          if (preslinkGuard.skippedSlideOps.length > 0) {
                            log(
                              `[chat-apply] skipped ${preslinkGuard.skippedSlideOps.length} terminal slide operation(s) targeting PresLink-managed slides`
                            );
                          }

                          // === Write phase: serialize via the operation coordinator ===
                          //
                          // Per f2_docs/powerpoint/undo-with-concurrency-behavior.md,
                          // `BeginUndo` is application-level — if file 2's writes
                          // start while file 1's writes are mid-flight, file 1's
                          // undo stack gets punctuated into a "Last" entry. So the
                          // *thinking* phase above runs in parallel across files
                          // (no PPT mutation), but every byte of mutation below
                          // goes through the coordinator's FIFO queue. The
                          // BeginUndo/EndUndo scope is owned by the coordinator
                          // and wraps just this run callback.
                          setLoadingStatus("Waiting to apply changes...");
                          if (abortSignal.aborted) {
                            trackQueryFailure("cancelled");
                            resolve();
                            return;
                          }

                          // === Persist the assistant turn BEFORE enqueuing the write
                          // flow ===
                          //
                          // The model's reply (modifications + thinking + citations)
                          // is fully known at this point. If we let the write flow
                          // own the mirror, a Stop-while-queued (BackgroundFlowBanner
                          // cancel before our flow's run() ever fires) silently
                          // discards the assistant turn — the user sees no record of
                          // what the model came up with.
                          //
                          // So we ingest declared citations, build the assistant
                          // message with `content: ""` (the AppliedChangesRow renders
                          // when apply succeeds), mirror it onto every affected
                          // slide tab synchronously, capture `affectedTabIds`, and
                          // THEN enqueue the write flow. If apply later fails or
                          // no-ops, the flow patches just the `content` field via
                          // `updateAssistantMessageContent` so the user still sees
                          // the apply status in the assistant bubble — preserving
                          // the pre-C4 UX on the happy/sad paths while making
                          // cancel-while-queued non-lossy.
                          mergeDeclaredCitations(turnCollector, modifications.citations);
                          const {
                            citations: compactedCitations,
                            citationsById: compactedCitationsById,
                            citedValuesByUuid: compactedCitedValuesByUuid,
                          } = compactTurnCitations(turnCollector);
                          const sourceCitationPayload = buildSourceSlideCitationPayload({
                            slides: modifications.slides || [],
                            selectedSlideId: selectedSlide?.id,
                            selectedSlideIndex: presentationContent.selectedSlideIndex,
                            citations: compactedCitations,
                            citationsById: compactedCitationsById,
                            citedValuesByUuid: compactedCitedValuesByUuid,
                          });
                          const modificationsForHistory: PresentationModification = {
                            ...modifications,
                            slides: (modifications.slides || []).map((slide) => {
                              const matchingSlide =
                                (slide.id
                                  ? presentationContent.slides.find(
                                      (deckSlide) => deckSlide.id === slide.id
                                    )
                                  : undefined) ??
                                presentationContent.slides.find(
                                  (deckSlide) => deckSlide.index === slide.index
                                );
                              return {
                                ...slide,
                                slideNumber: matchingSlide?.slideNumber ?? slide.index + 1,
                              };
                            }),
                          };
                          const assistantMessage: ChatMessage = {
                            id: generateId(),
                            role: "assistant",
                            content: "",
                            timestamp: Date.now(),
                            modifications: modificationsForHistory,
                            slideIndex: presentationContent.selectedSlideIndex,
                            slideNumber:
                              selectedSlide?.slideNumber ??
                              presentationContent.selectedSlideIndex + 1,
                            thinking: sanitizeUserFacingText(latestStreamingThinking),
                            agentTimeline: getCappedAgentTimelineForMessage(latestAgentTimeline),
                            ...(skillMetadata ? { skillMetadata } : undefined),
                            // Per-slide provenance for the source slide's Citations tab.
                            citations: sourceCitationPayload.citations,
                            citationsById: sourceCitationPayload.citationsById,
                            citedValuesByUuid: sourceCitationPayload.citedValuesByUuid,
                            // Turn-level retrieved sources for the chat bubble only.
                            retrievedCitations: compactedCitations,
                            retrievedCitedValuesByUuid: compactedCitedValuesByUuid,
                          };
                          console.log("[CHAT] assistantMessage", {
                            thinkingChars: assistantMessage.thinking?.length ?? 0,
                            citationCount: assistantMessage.citations?.length ?? 0,
                            declaredCitationCount: Array.isArray(modifications.citations)
                              ? modifications.citations.length
                              : 0,
                            citedValueCount: Object.keys(assistantMessage.citedValuesByUuid ?? {})
                              .length,
                          });
                          const affectedSlides = collectAffectedSlides(modifications).map(
                            (affected) => {
                              const matchingSlide =
                                (affected.id
                                  ? presentationContent.slides.find(
                                      (slide) => slide.id === affected.id
                                    )
                                  : undefined) ??
                                presentationContent.slides.find(
                                  (slide) => slide.index === affected.index
                                );
                              return {
                                ...affected,
                                slideNumber: matchingSlide?.slideNumber ?? affected.index + 1,
                              };
                            }
                          );
                          if (affectedSlides.length > 0) {
                            log(
                              `[CHAT] Mirroring assistant response to ${affectedSlides.length} affected slide session(s)`
                            );
                          }
                          let affectedTabIds: string[] = [sourceTabId];
                          applyToSourceTabs((prev) => {
                            const next = mirrorAssistantMessageToAffectedSlides(
                              prev,
                              sourceTabId,
                              assistantMessage,
                              affectedSlides,
                              userMessage
                            );
                            affectedTabIds = collectAffectedTabIds(
                              next,
                              sourceTabId,
                              affectedSlides
                            );
                            return next;
                          });

                          const endChatOperation = registerTabOperation(
                            authoritativeDocumentId,
                            sourceTabId,
                            "chat"
                          );
                          try {
                            setLoadingStatus("Applying changes...");
                            if (abortSignal.aborted) {
                              trackQueryFailure("cancelled");
                              resolve();
                              return;
                            }
                            const slideCount = modifications.slides?.length || 0;
                            const allShapes =
                              modifications.slides?.reduce(
                                (shapes, slide) => shapes.concat(slide.shapes || []),
                                [] as NonNullable<
                                  NonNullable<PresentationModification["slides"]>[number]["shapes"]
                                >
                              ) || [];
                            const imageShapes = allShapes.filter(
                              (s) => s.imageDataUri || s.imageBase64 || s.imageUrl
                            );
                            log(
                              `Received ${slideCount} slide modifications, ${allShapes.length} shapes (${imageShapes.length} with image payloads)`
                            );
                            if (imageShapes.length > 0) {
                              for (const s of imageShapes) {
                                if (s.imageDataUri) {
                                  log(`  🖼️ Shape ${s.id}: source=imageDataUri`);
                                } else if (s.imageBase64) {
                                  log(`  🖼️ Shape ${s.id}: source=imageBase64`);
                                } else {
                                  log(`  🖼️ Shape ${s.id}: imageUrl=${s.imageUrl?.slice(0, 80)}`);
                                }
                              }
                            }
                            const hasSlideOperations = (modifications.slides?.length || 0) > 0;
                            // Wave 4 fix 3: forward the bounds already resolved by the
                            // chat extract (the A2 `pageSetup: true` filter now returns
                            // them inline) so applyModifications doesn't issue a second
                            // round-trip to resolve them — which on hosts that don't
                            // expose dimensions via Office pageSetup falls through to an
                            // unfiltered RunOfficeExtractor call.
                            const preferredBoundsForApply =
                              presentationContent.slideWidth > 0 &&
                              presentationContent.slideHeight > 0
                                ? {
                                    slideWidth: presentationContent.slideWidth,
                                    slideHeight: presentationContent.slideHeight,
                                  }
                                : undefined;
                            const hasNewSlideActions = modifications.slides?.some(
                              (s) => s.action === "duplicate" || s.action === "insert"
                            );
                            let preNewSlideIds: Set<string> | null = null;
                            if (hasNewSlideActions) {
                              const preSlides = await listSlides({
                                forceRefresh: true,
                                presentationRef: presentationKey.presentationRef,
                              });
                              preNewSlideIds = new Set(preSlides.map((s) => s.id));
                            }

                            let baseApplySummary: ApplyResultSummary;
                            if (hasSlideOperations) {
                              // CRITICAL: do thread `presentationRef` into the APPLY
                              // step (unlike the extract / image steps above, which
                              // would trigger `bypassSharedCache` and degrade LLM
                              // input). The coordinator serializes flows FIFO but
                              // does NOT activate the target presentation — so
                              // without `presentationRef` here, `applyModifications`
                              // would land file 1's edits on whichever window the
                              // USER currently has focused (file 2), silently no-op
                              // on it because the shape IDs from file 1's extract
                              // don't match file 2's shapes, and then file 2's own
                              // write flow would arrive next and appear to "do
                              // nothing" because conflicting edits already touched
                              // those slides.
                              //
                              // Per f2_docs/powerpoint/undo-with-concurrency-behavior.md,
                              // `presentationRef` is the supported way to retarget
                              // mutations at a non-active presentation, and the
                              // coordinator already opened the BeginUndo scope against
                              // this same ref above. Threading it through here makes
                              // the apply side match the undo side.
                              baseApplySummary = await applyModifications(
                                modifications,
                                log,
                                undefined,
                                {
                                  preferredBounds: preferredBoundsForApply,
                                  presentationRef: presentationKey.presentationRef,
                                  brandId: brandId ?? undefined,
                                  onPreparedIntentBatch: async (preparedOperations) => {
                                    await runGoldenCaptureStep(
                                      goldenArtifactRun,
                                      log,
                                      "prepared_batch_operations.json",
                                      async () => {
                                        const preparedOperationsSnapshot = JSON.parse(
                                          JSON.stringify(preparedOperations)
                                        );
                                        await uploadGoldenArtifactJson(
                                          goldenArtifactRun!.runId,
                                          "prepared_batch_operations",
                                          preparedOperationsSnapshot
                                        );
                                      }
                                    );
                                  },
                                }
                              );
                              if (abortSignal.aborted) {
                                trackQueryFailure("cancelled");
                                resolve();
                                return;
                              }
                              // `syncTabsWithPresentation` calls `listSlides()` against
                              // the ACTIVE presentation; if the user has navigated to a
                              // different file while this write flow ran in the
                              // background, syncing now would overwrite the source
                              // file's tab list with another file's slides. Only sync
                              // when the source presentation is still the active one.
                              await syncSourceTabsWithPresentation("query-apply");
                            } else {
                              baseApplySummary = createSyntheticApplySummary(modifications);
                            }
                            baseApplySummary = appendModelDiagnosticsToApplySummary(
                              baseApplySummary,
                              modelDiagnostics
                            );
                            const appliedBaseCount = countSuccessfulOps(baseApplySummary);
                            if (appliedBaseCount > 0) {
                              log(
                                `✓ Changes applied (applied=${appliedBaseCount}, skipped=${baseApplySummary.skipped.count}, errors=${baseApplySummary.errors.count})`
                              );
                            } else {
                              log(
                                `No changes applied (applied=${appliedBaseCount}, skipped=${baseApplySummary.skipped.count}, errors=${baseApplySummary.errors.count})`
                              );
                            }

                            let newSlideRefs: SlideSessionRef[] = [];
                            if (preNewSlideIds && preNewSlideIds.size > 0) {
                              const postSlides = await listSlides({
                                forceRefresh: true,
                                presentationRef: presentationKey.presentationRef,
                              });
                              newSlideRefs = postSlides
                                .filter((s) => !preNewSlideIds!.has(s.id))
                                .map((s) => ({
                                  id: s.id,
                                  index: s.index,
                                  slideNumber: s.slideNumber,
                                }));
                              if (newSlideRefs.length > 0) {
                                log(
                                  `Detected ${newSlideRefs.length} new slide(s): ${newSlideRefs.map((s) => `Slide ${s.index + 1}`).join(", ")}`
                                );
                              }
                            }

                            // Attach per-slide citation messages for non-source slides,
                            // keyed by REAL slide id from every apply manifest seen this
                            // turn. This includes incremental apply_edits tool calls and
                            // the base terminal apply below; buildSlideCitationMessages
                            // collapses duplicate slide ids / citation ids.
                            {
                              const terminalSourceId = selectedSlide?.id;
                              const terminalCitationBindings = [
                                ...turnCitationBindings,
                                ...(baseApplySummary.citationManifest?.bindings ?? []),
                              ];
                              const terminalCitationMessages = buildNonSourceSlideCitationMessages({
                                bindings: terminalCitationBindings,
                                selectedSlideId: terminalSourceId,
                                selectedSlideIndex: presentationContent.selectedSlideIndex,
                                citations: compactedCitations,
                                citedValuesByUuid: compactedCitedValuesByUuid,
                                log,
                              });
                              if (terminalCitationMessages.length > 0) {
                                applyToSourceTabs((prev) =>
                                  terminalCitationMessages.reduce(
                                    (state, entry) =>
                                      attachSyntheticMessageToSlideTab(state, {
                                        slideId: entry.slide.id,
                                        slideIndex: entry.slide.index,
                                        slideNumber: entry.slide.slideNumber,
                                        message: entry.message,
                                      }),
                                    prev
                                  )
                                );
                                log(
                                  `[CITATIONS] Attached citation messages to ${terminalCitationMessages.length} slide(s).`
                                );
                              }
                            }

                            // Merge this turn's citations into the deck-wide pool
                            // (slide-1 `finster:citationPool` tag). We do this only
                            // after at least one operation applied successfully, so
                            // we don't pollute the pool with citations from rejected
                            // turns. Failures are non-fatal: the in-memory cache
                            // still has the data for the current session and the
                            // pool resyncs on the next successful turn.
                            if (turnCollector.citations.length > 0 && appliedBaseCount > 0) {
                              try {
                                await mergeIntoPool(turnCollector.citations, log);
                              } catch (poolErr) {
                                const poolMsg =
                                  poolErr instanceof Error ? poolErr.message : String(poolErr);
                                log(`[CITATIONS] mergeIntoPool failed: ${poolMsg}`);
                              }
                            }

                            // Legacy vision refinement removed: the unified agent
                            // self-reviews via get_slides_info + apply_edits, so chat
                            // edits land through the base apply path only. Held as null
                            // so the downstream summary merge stays unchanged.
                            const visionApplySummary: ApplyResultSummary | null = null;

                            const mergedSummary = mergeApplySummaries(
                              baseApplySummary,
                              visionApplySummary
                            );

                            mergedSummary.classification = classifyApplyResult(mergedSummary);
                            log(
                              `[APPLY_RESULT] classification=${mergedSummary.classification} ` +
                                `applied=${countSuccessfulOps(mergedSummary)} ` +
                                `skipped=${mergedSummary.skipped.count} errors=${mergedSummary.errors.count}`
                            );
                            const primaryIssue = findPrimaryIssue(mergedSummary);
                            if (primaryIssue) {
                              log(
                                `[APPLY_RESULT] primary_reason=${primaryIssue.code}: ${primaryIssue.message}`
                              );
                            }

                            const applyStatus = buildApplyStatusMessage(mergedSummary, query);

                            const changes: ChangeDescription[] = [];
                            let newSlideActionCounter = 0;

                            for (const slide of modifications.slides || []) {
                              const isNewSlideAction =
                                slide.action === "duplicate" || slide.action === "insert";
                              let newRefForSlide: SlideSessionRef | undefined;
                              if (isNewSlideAction) {
                                newRefForSlide = newSlideRefs[newSlideActionCounter];
                                const targetLabel = newRefForSlide
                                  ? `→ Slide ${newRefForSlide.index + 1}`
                                  : "";
                                changes.push({
                                  action: "create",
                                  description:
                                    slide.action === "insert"
                                      ? `Slide ${slide.index + 1}: Created new slide ${targetLabel}`.trim()
                                      : `Slide ${slide.index + 1}: Duplicated ${targetLabel}`.trim(),
                                });
                                newSlideActionCounter++;
                              }

                              for (const shape of slide.shapes || []) {
                                let description = "";
                                if (shape.action === "create") {
                                  const posInfo =
                                    shape.left !== undefined || shape.top !== undefined
                                      ? ` at (${shape.left ?? 0}, ${shape.top ?? 0})`
                                      : "";
                                  const sizeInfo =
                                    shape.width !== undefined || shape.height !== undefined
                                      ? ` ${shape.width ?? "auto"}×${shape.height ?? "auto"}pt`
                                      : "";

                                  if (shape.chart) {
                                    const chartTitle =
                                      shape.chart.title?.text ||
                                      shape.chart.series?.[0]?.name ||
                                      "data";
                                    const chartTypeLabel = getFriendlyChartTypeLabel(
                                      shape.chart.chartType
                                    );
                                    description = `Added ${chartTypeLabel} chart: ${chartTitle}${posInfo}`;
                                  } else if (shape.table) {
                                    const cols = shape.table.rows?.[0]?.length || 0;
                                    description = `Added table (${shape.table.rows?.length || 0}×${cols})${posInfo}${sizeInfo}`;
                                  } else if (
                                    shape.imageDataUri ||
                                    shape.imageBase64 ||
                                    shape.imageUrl
                                  ) {
                                    const imageUrl = shape.imageUrl ?? "";
                                    const isCompanyLogo = isLogoImageUrl(imageUrl);
                                    const domain = isCompanyLogo
                                      ? "company logo"
                                      : imageUrl
                                        ? imageUrl.slice(0, 40)
                                        : "inline image payload";
                                    description = `Added image (${domain})${posInfo}${sizeInfo}`;
                                  } else {
                                    const textPreview = shape.text?.slice(0, 50) || "empty";
                                    description = `Added text: "${textPreview}${shape.text && shape.text.length > 50 ? "..." : ""}"${posInfo}${sizeInfo}`;
                                  }
                                } else if (shape.action === "modify") {
                                  const parts: string[] = [];
                                  const tableTextUpdates =
                                    shape.table?.textUpdates && shape.table.textUpdates.length > 0
                                      ? shape.table.textUpdates
                                      : shape.cellUpdates;
                                  if (tableTextUpdates && tableTextUpdates.length > 0) {
                                    parts.push(
                                      `${tableTextUpdates.length} table cell${tableTextUpdates.length > 1 ? "s" : ""}`
                                    );
                                  }
                                  if (
                                    shape.table?.structureOps &&
                                    shape.table.structureOps.length > 0
                                  ) {
                                    parts.push(
                                      `${shape.table.structureOps.length} table structure op(s)`
                                    );
                                  }
                                  if (
                                    shape.table?.styleUpdates &&
                                    shape.table.styleUpdates.length > 0
                                  ) {
                                    parts.push(
                                      `${shape.table.styleUpdates.length} table style update(s)`
                                    );
                                  }
                                  if (shape.table?.clearOps && shape.table.clearOps.length > 0) {
                                    parts.push(`${shape.table.clearOps.length} table clear op(s)`);
                                  }
                                  if (shape.table?.layoutSpec) {
                                    parts.push("table relayout");
                                  }
                                  if (shape.textReplacements && shape.textReplacements.length > 0) {
                                    const r = shape.textReplacements[0];
                                    parts.push(
                                      `"${r.find}" → "${r.replace}"${shape.textReplacements.length > 1 ? ` +${shape.textReplacements.length - 1} more` : ""}`
                                    );
                                  }
                                  if (shape.text !== undefined) {
                                    const textPreview = shape.text.slice(0, 40);
                                    parts.push(
                                      `text → "${textPreview}${shape.text.length > 40 ? "..." : ""}"`
                                    );
                                  }
                                  if (shape.left !== undefined || shape.top !== undefined) {
                                    const posStr = `(${shape.left ?? "–"}, ${shape.top ?? "–"})`;
                                    parts.push(`position → ${posStr}`);
                                  }
                                  if (shape.width !== undefined || shape.height !== undefined) {
                                    const sizeStr = `${shape.width ?? "–"}×${shape.height ?? "–"}`;
                                    parts.push(`size → ${sizeStr}`);
                                  }
                                  description = `Updated ${parts.join(", ") || "shape"}`;
                                } else if (shape.action === "delete") {
                                  description = "Removed shape";
                                }
                                const slideLabel = newRefForSlide
                                  ? `Slide ${newRefForSlide.index + 1}`
                                  : `Slide ${slide.index + 1}`;
                                changes.push({
                                  action: shape.action,
                                  description: `${slideLabel}: ${description}`,
                                });
                              }
                            }

                            const createdCharts: ChartInfo[] = [];
                            for (const slide of modifications.slides || []) {
                              for (const shape of slide.shapes || []) {
                                if (shape.action === "create" && shape.chart) {
                                  createdCharts.push(shape.chart);
                                }
                              }
                            }

                            const queryResult: QueryResult = {
                              success: applyStatus.success,
                              message: sanitizeUserFacingText(applyStatus.message),
                              changes: applyStatus.success ? changes : undefined,
                              thinking: sanitizeUserFacingText(latestStreamingThinking),
                              charts: createdCharts.length > 0 ? createdCharts : undefined,
                            };

                            // Patch the pre-mirrored assistant message's `content`
                            // whenever AppliedChangesRow must not render. A failed
                            // apply can still have model-derived change descriptions,
                            // but only a successful apply should leave empty content
                            // plus populated modifications for ChatHistory to render as
                            // an applied-changes card.
                            const hasAppliedChangesRow = applyStatus.success && changes.length > 0;
                            if (!hasAppliedChangesRow) {
                              const patchedContent = sanitizeUserFacingText(applyStatus.message);
                              // Strip `modifications` on the failure path so the
                              // `AppliedChangesRow` (which gates on
                              // `modifications.slides.length > 0`) doesn't render a
                              // success-looking card with the model's intent
                              // summary right next to the apply-failed bubble.
                              applyToSourceTabs((prev) =>
                                updateAssistantMessageContent(
                                  prev,
                                  assistantMessage.id,
                                  patchedContent,
                                  {
                                    clearModifications: true,
                                  }
                                )
                              );
                            }
                            // Result panel is per-tab; if the user has switched away,
                            // there's no panel to update — skip the result write rather
                            // than leak file A's result into file B's view.
                            if (getDocumentId() === authoritativeDocumentId) {
                              setResultsByTabId((prev) => {
                                const next = { ...prev };
                                for (const tabId of affectedTabIds) {
                                  next[tabId] = queryResult;
                                }
                                return next;
                              });
                            }
                            await syncSourceTabsWithPresentation("query-complete");
                            await runGoldenCaptureStep(
                              goldenArtifactRun,
                              log,
                              "after.pptx",
                              async () => {
                                const afterDeckBase64 =
                                  await exportPresentationAsBase64Compressed();
                                await uploadGoldenArtifactPptx(
                                  goldenArtifactRun!.runId,
                                  "after",
                                  afterDeckBase64
                                );
                              }
                            );
                            trackQuerySuccess();
                          } catch (writeFlowError) {
                            log(
                              `Chat write flow ended with error: ${writeFlowError instanceof Error ? writeFlowError.message : String(writeFlowError)}`
                            );
                            trackQueryFailure("write_flow_error");
                            setResult({
                              success: false,
                              message: toUserFriendlyRequestError(
                                writeFlowError instanceof Error
                                  ? writeFlowError.message
                                  : String(writeFlowError)
                              ),
                              thinking: sanitizeUserFacingText(latestStreamingThinking),
                            });
                          } finally {
                            endChatOperation();
                          }
                          resolve();
                        } catch (applyError) {
                          trackQueryFailure("apply_error");
                          setResult({
                            success: false,
                            message:
                              applyError instanceof Error
                                ? applyError.message
                                : "Failed to apply changes",
                            thinking: sanitizeUserFacingText(latestStreamingThinking),
                          });
                          await syncSourceTabsWithPresentation("query-apply-error");
                          resolve();
                        }
                      },
                      onError: (error: string) => {
                        void (async () => {
                          try {
                            if (abortSignal.aborted) {
                              trackQueryFailure("cancelled");
                              return;
                            }
                            setLoadingStatus("Finishing thought process...");
                            try {
                              const narratorFinalization = await narratorSink.finalize();
                              if (narratorFinalization.timedOut) {
                                log("[NARRATOR] finalization timed out; completing error response");
                              }
                            } catch (finalizeError) {
                              log(
                                `[NARRATOR] finalization failed while completing error response: ${
                                  finalizeError instanceof Error
                                    ? finalizeError.message
                                    : String(finalizeError)
                                }`
                              );
                            }
                            if (abortSignal.aborted) {
                              trackQueryFailure("cancelled");
                              return;
                            }
                            log(`❌ AI request error: ${error}`);
                            log(
                              `💡 TIP: Open Safari > Develop > Microsoft PowerPoint to see detailed console logs`
                            );
                            console.error("[AI REQUEST ERROR]", error);
                            const userFriendlyMessage = toUserFriendlyRequestError(error);
                            // Surface the failure in the chat itself, not just the
                            // result side-panel. Without this, an LLM/network error
                            // leaves the user's prompt hanging with no assistant
                            // reply at all — visually identical to a still-loading
                            // turn after the spinner clears.
                            const errorAssistantMessage: ChatMessage = {
                              id: generateId(),
                              role: "assistant",
                              content: sanitizeUserFacingText(userFriendlyMessage),
                              timestamp: Date.now(),
                              slideIndex: activeTab.session.slideIndex,
                              slideNumber:
                                activeTab.session.slideNumber ?? activeTab.session.slideIndex + 1,
                              thinking: sanitizeUserFacingText(latestStreamingThinking),
                              agentTimeline: getCappedAgentTimelineForMessage(latestAgentTimeline),
                              ...(skillMetadata ? { skillMetadata } : undefined),
                            };
                            applyToSourceTabs((prev) => {
                              const currentTab = prev.tabs.find((t) => t.id === sourceTabId);
                              if (!currentTab) return prev;
                              return updateTab(prev, sourceTabId, {
                                session: {
                                  ...currentTab.session,
                                  messages: [...currentTab.session.messages, errorAssistantMessage],
                                  updatedAt: Date.now(),
                                },
                              });
                            });
                            setResult({
                              success: false,
                              message: userFriendlyMessage,
                              thinking: sanitizeUserFacingText(latestStreamingThinking),
                            });
                            trackQueryFailure("model_error");
                          } catch (handlerError) {
                            log(
                              `Failed to handle model error: ${
                                handlerError instanceof Error
                                  ? handlerError.message
                                  : String(handlerError)
                              }`
                            );
                          } finally {
                            resolve();
                          }
                        })();
                      },
                    },
                    slideImageBase64,
                    chatHistoryForModel,
                    toolPolicy,
                    {
                      signal: abortSignal,
                      agentToolContext,
                      ...(preludeUserMessages ? { preludeUserMessages } : {}),
                      langfuseContext: langfuseRunContext,
                      langfusePhase: "chat",
                    }
                  );
                });
              } catch (error) {
                if (abortSignal.aborted) {
                  trackQueryFailure("cancelled");
                  if (ownsLoadingState()) {
                    setResult(null);
                  }
                  return;
                }
                console.error("Query failed:", error);
                const errorMessage = error instanceof Error ? error.message : String(error);
                log(`Error: ${errorMessage}`);
                trackQueryFailure("query_error");
                setResult({
                  success: false,
                  message: toUserFriendlyRequestError(errorMessage),
                });
              } finally {
                if (ownsLoadingState()) {
                  setIsLoading(false);
                  setLoadingStatus("");
                }
              }
            };
            // Phase split (per f2_docs undo-with-concurrency-behavior.md):
            // - Reads + LLM streaming ("thinking") run OUTSIDE the coordinator so
            //   the user can chat on file 2 while file 1's writes are still being
            //   applied. Per the undo doc, only BeginUndo/EndUndo (application-
            //   level punctuation) fragments other presentations' undo stacks;
            //   reads + LLM calls do not touch the undo stack.
            // - The actual mutating work (`applyModifications`, the vision
            //   refinement pass, and the post-apply tab sync) is enqueued INSIDE
            //   `onComplete` as a separate "Apply" flow. That flow is what owns
            //   the BeginUndo/EndUndo scope, so writes across files serialize
            //   into a clean undo stack per file.
            try {
              await runChatPipeline();
            } catch (error) {
              if (abortSignal.aborted) {
                trackQueryFailure("cancelled");
                if (ownsLoadingState()) {
                  clearLoadingState();
                  setResult(null);
                }
                return;
              }
              trackQueryFailure("thinking_phase_error");
              log(
                `Chat thinking phase ended with error: ${error instanceof Error ? error.message : String(error)}`
              );
              if (ownsLoadingState()) {
                clearLoadingState();
                const message = error instanceof Error ? error.message : String(error);
                setResult({
                  success: false,
                  message: toUserFriendlyRequestError(message),
                });
              }
            } finally {
              // Release the thinking-phase ticket so `reconcileLoadingWithRegistry`
              // can again demote any leftover `isLoading=true` on this tab to
              // "Interrupted" on future loads. The whole `runChatPipeline()` —
              // including the inner write-flow `enqueueFlow` it awaits — has
              // returned by here, so the coordinator's own registry takes over
              // ownership of liveness exactly when this ticket retires.
              unregisterThinkingTicket();
              abortPendingSetup();
              settleReviewOperationActivity();
              skillResolveSingleFlightRef.current?.release(submissionId);
              if (activeQueryAbortRef.current[sourceTabId]?.controller === abortController) {
                delete activeQueryAbortRef.current[sourceTabId];
              }
              releaseUnusedReviewGeneration();
            }
          } catch (error) {
            failAfterQueryAccepted(error);
          }
        }
        void continueAcceptedQuery();
        return true;
      } catch (error) {
        failAfterQueryAccepted(error);
        return false;
      }
    },
    [
      activeTab,
      updateActiveTab,
      taskContext,
      aiMode,
      syncTabsWithPresentation,
      setTabsState,
      brandId,
      plannerMode,
      companyId,
      accountScopeKey,
      pendingDeckPlan,
    ]
  );

  useEffect(() => {
    if (pendingFixQuery && aiMode === "chat") {
      handleQuery(pendingFixQuery);
      setPendingFixQuery(null);
    }
  }, [pendingFixQuery, aiMode, handleQuery]);

  handleQueryRef.current = handleQuery;

  return {
    result,
    setResult,
    handleQuery,
    cancelQuery,
    cancelAllQueries,
    handleReplaceSkillRun,
    pendingFixQuery,
    setPendingFixQuery,
    cameFromReview,
    setCameFromReview,
  };
};

export type { QueryResult, ChangeDescription };
